import type {
  _ColumnAlign,
  _ColumnVisibility,
  _RebuildType,
  ColumnDisplayDef,
  DisplayMode,
  ListDefinition,
  OvlSysColumn,
  RowFnTrackingInterval,
  RowFunctionResultToSet,
  Sort,
  SYSColumnDef,
  SYSTableData,
  SYSTableDef,
  TableDataAndDef,
} from "./table"

import type { SYSField, SYSFormState } from "../forms/actions"
import type { ListState } from "../forms/controls/listcontrol"
import type {
  _ClientConversion,
  DataType,
  FieldFormat,
  LookupResultDataType,
  SYSFormField,
} from "../forms/sysformelement"

import {
  FormFnCan_Visibility,
  HookFieldGetList_ReturnType,
  HookFieldGetValueRender_ReturnType,
  HookFieldGetValueRender_Type,
  HookFormCustomControlRender,
  HookFormCustomControlRender_Type,
  HookFormGetBodyRender,
  HookFormGetBodyRender_ReturnType,
  HookFormGetBodyRender_Type,
  HookLookupRowRefreshed,
  HookLookupRowRefreshed_Type,
  HookLookupsRefresh_Type,
  HookTableCustomFilter_Type,
  HookTableCustomSort_ReturnType,
  HookTableCustomSort_Type,
  HookTableFilterSortSettingsDifferentiator,
  HookTableFilterSortSettingsDifferentiator_ReturnType,
  HookTableFilterSortSettingsDifferentiator_Type,
  HookTableGroupSortValue,
  HookTableInit,
  HookTableRowAttributesGetAttributes,
  HookTableRowAttributesGetAttributes_Type,
  HookTableRowFn_Type,
  HookTableRowFnCan_Type,
  HookTableRowFnConfirm,
  HookTableRowFnConfirm_Type,
  HookTableRowFnConfirmToSet_Type,
  //HookTableServerBeforeMergeRow_ReturnType,
  HookTableServerBeforeMergeRow_Type,
  HookTableSetColumnVisibility,
  HookTableSetColumnVisibility_Type,
  RowFnConfirmContentFieldsReset_Type,
  ServerData_ChangedType,
  TableRowFnProcessRawFeedbackSummaryCount_Type,
} from "../../global/hooks"

import type {
  SYSActions,
  SYSFeature,
  SYSForm,
  SYSLongRunningProcess,
  SYSState,
  SYSTableDefIds,
} from "../../index"

import { html, ovl, SYSConfig } from "../../"
import { cachedRendererFn } from "./table"

import {
  _HasPermission,
  getDateValue,
  getDecimalValue,
  getIntegerValue,
  getOnlyDateISOString,
  isMobile,
  stringifyReplacer,
  T,
  UIContextType,
} from "../../global/globals"
import {
  HookFieldGetList,
  HookFieldGetValueRender,
  HookLookupsRefresh,
  HookTableCustomFilter,
  HookTableCustomSort,
  HookTableGroupValue,
  HookTableRowFn,
  HookTableRowFnCan,
} from "../../global/hooks"

import { _GetListValue } from "../forms/controls/helpers"
import {
  ProcessIsRunning,
  ProcessStart,
} from "../longrunningprocesstracker/processapi"

import { Data } from "../../dataapi"
import { FeatureInfo, SortGet } from "../../global/globalapi"
import { _RenderTracking } from "../../tracker/tracker"
import { DialogIsOpen, DialogOkCancel } from "../dialog/dialogapi"
import { FieldSetVal, SYSFields } from "../forms/fieldapi"
import {
  FormGet,
  FormGetControl,
  FormHasVisibleSummaryErrors,
  FormInit,
  FormReset,
  FormSetUnDirty,
} from "../forms/formapi"
import {
  _ProcessReturnType,
  ProcessSnackType,
} from "../longrunningprocesstracker/longrunningprocesstracker"
import { ScreenCurrent, ScreenCurrentState } from "../screenapi"
import { SnackAdd, SnackTrackedReplaceContent } from "../snackapi"
import { _LocalDataSyncType } from "./actions"
import {
  _GetTableOptions,
  TableAttributeFilter,
  TableColumnFilter,
  TableCustomFilter,
  TableGet,
  TableRebuild,
  TableStaticFilterGet,
} from "./tableapi"
import { _controlInit } from "../../helpers"

export let cachedListFn: Map<string, any> = new Map<string, any>()

export const _GetAlign = (align: _ColumnAlign) => {
  switch (align) {
    case "center":
      return "ovlca_c"
    case "right":
      return "ovlca_r"
    default:
      return ""
  }
}

export const _GetColumnInTableVisibility = (visible: _ColumnVisibility) => {
  let validColumn = true
  if (!visible) {
    throw Error(
      "SYS Table: Either Initialise Table first or define column.ui.visibility"
    )
  }
  if (isMobile()) {
    if (visible.indexOf("TableNotMobile") > -1) {
      validColumn = false
    }
  } else {
    if (visible.indexOf("TableOnlyMobile") > -1) {
      validColumn = false
    }
  }
  if (visible.indexOf("Table") < 0) {
    validColumn = false
  }
  return validColumn
}

export const getListValue = (
  key: string,
  value: any,
  feature: SYSFeature,
  list: ListState,
  formState?: SYSFormState,
  lookupColumnKey?: string
): string => {
  if (list && feature) {
    let cachedListKey =
      <string>feature + (formState ? formState.subFeature : "") + key
    let listFn = cachedListFn.get(cachedListKey)
    if (!listFn) {
      listFn =
        ovl.getActions().app.features[feature]?.hooks?.[
          HookFieldGetList.replace(
            "{subFeature}",
            formState ? formState.subFeature : ""
          ).replace("{fieldKey}", key)
        ]
      if (!listFn) {
        throw new Error(
          "Listfunction: " +
            HookFieldGetList.replace(
              "{subFeature}",
              formState ? formState.subFeature : ""
            ).replace("{fieldKey}", key) +
            " not found!"
        )
      }
      cachedListFn.set(cachedListKey, listFn)
    }
    let listdata: HookFieldGetList_ReturnType = _GetFieldList(
      listFn,
      key,
      formState,
      formState?.tableDefId
    )

    value = _GetListValue(list, value, listdata, lookupColumnKey)
  }
  return value
}

export const getDisplayValue = (
  key: string,
  col: ColumnDisplayDef,
  value: any,
  feature: SYSFeature,
  formState?: SYSFormState,
  tableDefId?: SYSTableDefIds,
  lookupColumnKey?: string
): string => {
  if (
    (col.list && value === undefined) ||
    (!formState &&
      col.ui &&
      col.ui.displayEmptyIfValue !== undefined &&
      value === col.ui.displayEmptyIfValue)
  ) {
    return ""
  }
  let format
  if (col.ui) {
    format = col.ui.format
  }
  let type: LookupResultDataType = col.type
  let isHtmlType = false
  if (col.list && feature) {
    let cachedListKey = <string>feature + key
    let listFn = cachedListFn.get(cachedListKey)
    if (!listFn) {
      listFn =
        ovl.getActions().app.features[feature]?.hooks?.[
          HookFieldGetList.replace(
            "{subFeature}",
            formState ? formState.subFeature : ""
          ).replace("{fieldKey}", key)
        ]
      if (!listFn) {
        throw new Error(
          "Listfunction: " +
            HookFieldGetList.replace(
              "{subFeature}",
              formState ? formState.subFeature : ""
            ).replace("{fieldKey}", key) +
            " not found!"
        )
      }
      cachedListFn.set(cachedListKey, listFn)
    }
    let listdata: HookFieldGetList_ReturnType = _GetFieldList(
      listFn,
      key,
      formState,
      tableDefId,
      undefined,
      true
    )

    value = _GetListValue(col.list, value, listdata, lookupColumnKey, true)

    if (lookupColumnKey) {
      type = listdata.lookupDef?.[lookupColumnKey]?.type
    } else {
      type = listdata.lookupDef?.[col.list.displayField]?.type
    }
    if (type === "html") {
      isHtmlType = true
    }
  }
  if (isHtmlType) {
    return value
  }
  let res = GetDisplayValueByType(format, type, value)
  if (res === null) {
    res = ""
  }
  return res
}

export let _cachedFieldLists = new Map<string, HookFieldGetList_ReturnType>()

export const _GetFieldListCacheKey = (
  fieldKey: string,
  tableDefId: SYSTableDefIds = undefined,
  formState: SYSFormState = undefined
): string => {
  return `@@table:${tableDefId ? tableDefId : ""}@@form:${
    formState ? `${formState.formType}${formState.formId}` : ""
  }@@fieldkey:${fieldKey}`
}

export const _GetFieldList = (
  listFn: any = undefined,
  fieldKey: string,
  formState: SYSFormState,
  tableDefId: SYSTableDefIds,
  forceCacheBust?: boolean,
  notFullList?: boolean
): HookFieldGetList_ReturnType => {
  let cacheKey = _GetFieldListCacheKey(fieldKey, tableDefId, formState)
  let list
  if (!forceCacheBust) {
    list = _cachedFieldLists.get(cacheKey)
    if (list) {
      //console.log("from cache ", fieldKey, cacheKey)
      return list
    }
  }
  if (listFn === undefined) {
    let feature = formState?.feature
    if (feature === undefined) {
      feature = TableGet(tableDefId).def.feature
    }

    let fnKey = HookFieldGetList.replace(
      "{subFeature}",
      formState ? formState.subFeature : ""
    ).replace("{fieldKey}", fieldKey)
    listFn = ovl.getActions().app.features[feature]?.hooks?.[fnKey]
  }
  list = listFn({ formState, needsFullList: notFullList !== true })
  //if (!formState || !formState.firstInitOrSituationChanged) {
  //console.log("set cache ", fieldKey, cacheKey)
  _cachedFieldLists.set(cacheKey, list)
  // }
  return list
}

export const GetDisplayValueByType = (
  format: FieldFormat,
  type: any,
  value: any
) => {
  switch (type) {
    case "date":
      if (!value) {
        return ""
      }
      return getDateValue(value, format)
    case "datetime":
      if (!value) {
        return ""
      }
      return getDateValue(value, format)

    case "decimal":
      if ((!value && value != 0) || value === "") {
        return ""
      }
      return getDecimalValue(value, format)
    case "int":
      if ((!value && value != 0) || value === "") {
        return ""
      }
      return getIntegerValue(value, format)

    case "bool":
      return value

    case "asset":
      return value
    default:
      if (!value && value !== 0) {
        value = ""
      }
      return value.toString()
  }
}

let customRendering = false

export const GetRowRawValue = (
  def: SYSTableDef,
  columnKey: string,
  row: any,
  rowKey: any,
  displayMode: DisplayMode = "Table"
) => {
  let rendererFn = GetRendererFn(
    def.feature,
    cachedRendererFn,
    HookFieldGetValueRender.replace("{subFeature}", ""),
    columnKey
  )
  if (rendererFn) {
    var customRendererResult = rendererFn(<HookFieldGetValueRender_Type>{
      tableDef: def,
      rowKey,
      row,
      columnKey,
      displayMode,
      comp: this,
    })
    if (!customRendererResult) {
      // return raw value
      return row[columnKey]
    }
    return customRendererResult.valueRaw !== undefined
      ? customRendererResult.valueRaw
      : customRendererResult.res
  }
  return row[columnKey]
}

export const GetRowDisplayValue = (
  def: SYSTableDef,
  columnKey: string,
  row: any,
  rowKey: any,
  displayMode: DisplayMode = "Table",
  ignoreRenderBarrier?: boolean,
  rendererFn?: any
) => {
  // check for custom renderer
  let rowPart
  let customRendererResult: HookFieldGetValueRender_ReturnType
  // use a barrier (customRendering bool) if getrowdisplayvalue is calling itself (eg. calling CellTableVal inside a FieldValue (Rendering) Hook will call GetRow...)
  if (!customRendering || ignoreRenderBarrier) {
    if (!rendererFn) {
      rendererFn = GetRendererFn(
        def.feature,
        cachedRendererFn,
        HookFieldGetValueRender.replace("{subFeature}", ""),
        columnKey
      )
    }
    if (rendererFn) {
      customRendering = true
      customRendererResult = rendererFn(<HookFieldGetValueRender_Type>{
        tableDef: def,
        rowKey,
        row,
        columnKey,
        displayMode,
        comp: this,
      })
      if (customRendererResult) {
        rowPart = customRendererResult.res
      }
      customRendering = false
    }
  }
  let col = def.columns[columnKey]
  if (!customRendererResult) {
    if (col.nullHandling.enabled && row[columnKey] === null) {
      rowPart = col.nullHandling.displayValue
    } else if (col.control === "checkbox") {
      if (row[columnKey] === col.ui.checkedValue) {
        rowPart = def.options.controlsRendering.checkbox.table.checked
      } else {
        rowPart = def.options.controlsRendering.checkbox.table.unchecked
      }
    } else {
      rowPart = getDisplayValue(
        columnKey,
        col,
        row[columnKey],
        def.feature,
        undefined,
        def.id
      )
    }
  }
  return rowPart
}

// same applies to this method...use it from your customdeleters
export const deleteTableRow = (
  tableDataAndDef: TableDataAndDef,
  key: string
) => {
  let data = tableDataAndDef.data
  Object.keys(tableDataAndDef.data.tableDef).forEach((k) => {
    let def = tableDataAndDef.data.tableDef[k] as SYSTableDef
    if (!def.uiState) {
      return
    }
    let tableGroupHeaderBreaks = def.uiState.tableGroupHeaderBreaks
    let tableGroupFooterBreaks = def.uiState.tableGroupFooterBreaks

    let tableGroupResults = def.uiState.tableGroupResults
    delete tableGroupHeaderBreaks[key]
    delete tableGroupFooterBreaks[key]

    delete tableGroupResults[key]

    delete tableDataAndDef.def.uiState.selectedRow[key]

    // let wasAdd =
    //   (<OvlSysColumn>tableDataAndDef.data.data[key]?.OvlSysState).rowState
    //     .state === "Add"

    // // delete any errors on processes for that rowkey
    // let res = ProcessGetRowFnsFeedback(def.id)
    // res.involvedProcessInstances.forEach((s) => {
    //   delete s.feedback[key]
    // })

    // let i = def.uiState.dataFilteredAndSorted.indexOf(key)
    // if (i > -1) {
    //   def.uiState.dataFilteredAndSorted.splice(i, 1)
    // }
    // if (!def.uiState.rebuild.deletedKeys[key]) {
    //   if (def.id === tableDataAndDef.def.id) {
    //     def.uiState.rowsCount--
    //   }
    def.uiState.rebuild.deletedKeys[key] = true
    //}

    // fixPagingAndSetCountAndNeedsRefresh(
    //   Object.keys(tableDataAndDef.data.tableDef).map(
    //     (m) => tableDataAndDef.data.tableDef[m]
    //   ),
    //   !wasAdd,
    //   def.id,
    //   true,
    //   data.data[key]
    // )
  })
  let index = data.index
  if (data.data[key]) {
    let rowId = data.data[key][tableDataAndDef.def.database.dataIdField]
    delete index[rowId]
    delete data.data[key]
  }
}

// export const fixPagingAndSetCountAndNeedsRefresh_ToBeRemoved = (
//   defs: SYSTableDef[],
//   needsRefresh: boolean = false,
//   currentDefId: SYSTableDefIds,
//   wasDelete: boolean = false,
//   changedFields: { [key: string]: any } = undefined
// ) => {
//   defs
//     .filter((f) => f.initialised === true)
//     .forEach((def) => {
//       let dataFilteredAndSorted = def.uiState.dataFilteredAndSorted
//       let count = dataFilteredAndSorted.length
//       def.uiState.rowsCount = count
//       if (wasDelete) {
//         def.uiState.rebuild.modified = true
//       } else {
//         def.uiState.rebuild.added = true
//       }
//       if (needsRefresh === true && def.uiState.rebuild.needsRefresh !== true) {
//         if (def.id !== currentDefId) {
//           //check if staticfilter is involved in changedfields
//           let staticFilter = def.uiState.filter.filter.static
//           if (
//             staticFilter &&
//             Object.keys(staticFilter).length > 0 &&
//             changedFields
//           ) {
//             needsRefresh = Object.keys(changedFields).some((k1) =>
//               Object.keys(staticFilter).some(
//                 (k) => staticFilter[k] === changedFields[k1]
//               )
//             )
//           }
//         }
//         if (needsRefresh) {
//           // if it was  a delete needsRefresh should be set only if aggregates involved
//           if (
//             (def.uiState.rowsCount > 1 || !wasDelete) &&
//             (!wasDelete ||
//               (wasDelete &&
//                 !def.options.tableGroupAggregates.disabled &&
//                 (def.options.tableGroupAggregates.aggregates.length > 0 ||
//                   def.options.tableGroupAggregates.groups.length > 0)))
//           ) {
//             def.uiState.rebuild.needsRefresh = true
//           }
//         }
//         if (def.uiState.rowsCount <= 1) {
//           def.uiState.rebuild.needsRefresh = false
//         }
//       }

//       if (def.features.page) {
//         let paging = def.uiState.paging
//         let pages = Math.ceil(count / paging.pageSize) - 1
//         if (pages > -1) {
//           if (paging.page > pages) {
//             paging.page = pages
//           } else if (paging.page < 0) {
//             paging.page = 0
//           }
//         }
//       }
//     })
// }

export const _GetEntityNameMulti = (entityTranslationKey: string): string => {
  let multiEntityTranslationKey = `${entityTranslationKey}Multi`
  if (ovl.getState().ovl.language[multiEntityTranslationKey]) {
    entityTranslationKey = multiEntityTranslationKey
  } else {
    entityTranslationKey = "SYSDefaultEntityNameMulti"
  }
  return entityTranslationKey
}

export const _GetTableDialogTitle = (
  def: SYSTableDef,
  actionName: string,
  count: number,
  rows: any,
  rowKey: string,
  ignoreId?: boolean
) => {
  let selectionText = ""
  let idTranslation = ""
  let entityTranslationKey = def.title.entityTitle.entityNameTranslationKey
  if (count > 1) {
    entityTranslationKey = _GetEntityNameMulti(entityTranslationKey)
    selectionText = T("SYSTableDialogTitleSelected")
  } else {
    if (
      !def.title.entityTitle.tableDialogTitleDoNotDisplayId &&
      !ignoreId &&
      rowKey &&
      def.uiState.columnsRenderingState.visibleInTable.indexOf(
        def.database.dataIdField
      ) > -1
    ) {
      idTranslation = "Id"

      let idColumnTranslationKey
      if (def.columns[def.database.dataIdField]?.ui?.labelTranslationKey) {
        idColumnTranslationKey =
          def.columns[def.database.dataIdField].ui.labelTranslationKey
      } else {
        let translationPrefix
        if (
          def.options.labelAndColumnTitleHandling?.translationPrefix !==
          undefined
        ) {
          translationPrefix =
            def.options.labelAndColumnTitleHandling.translationPrefix
          if (
            def.options.labelAndColumnTitleHandling
              .fallbackTranslationPrefix !== undefined &&
            ovl.getState().ovl.language.translations[
              `${translationPrefix}${def.database.dataIdField}`
            ] === undefined
          ) {
            translationPrefix =
              def.options.labelAndColumnTitleHandling.fallbackTranslationPrefix
          }
        }
        if (translationPrefix) {
          idColumnTranslationKey = `${translationPrefix}${def.database.dataIdField}`
        }
      }

      if (idColumnTranslationKey) {
        idTranslation = T(idColumnTranslationKey)
      }
      let val = GetRowDisplayValue(
        def,
        def.database.dataIdField,
        rows[rowKey],
        rowKey,
        "DialogTitle"
      )
      if (typeof val !== "string") {
        val = rows[rowKey][def.database.dataIdField]
      }
      idTranslation = ` (${idTranslation}: ${val})`
    }
  }
  let entityName = T(entityTranslationKey)
  let translationKey = def.title.entityTitle.tableDialogTitleTranslationKey
  if (count > 1) {
    let multiTranslationKey = `${translationKey}Multi`
    if (ovl.getState().ovl.language.translations[multiTranslationKey]) {
      translationKey = multiTranslationKey
    }
  }

  return T(translationKey, [
    selectionText,
    entityName,
    idTranslation,
    actionName,
    count,
  ])
}

export const initTableState = async (
  def: SYSTableDef,
  data: SYSTableData,
  defId: SYSTableDefIds,
  onlyColumns?: boolean
) => {
  let wasInitialised = def.initialised === true
  if (!def.initialised || onlyColumns) {
    // prepare state/ complete state / set sensible defaults
    if (!def.initialised) {
      def.initialised = true
      def.id = defId
      if (data === undefined) {
        throw Error("ovl state init: data object not present")
      }
      if (data.data === undefined) {
        data.data = {}
      }
      if (data.clientConversion === undefined) {
        data.clientConversion = {}
      }
      // if (data.schema === undefined) {
      //   data.schema = {}
      // }
      if (data.offline === undefined) {
        data.offlineSeq = 0
        data.offline = {
          addedKeys: {},
          deletedKeys: {},
          errors: {},
          updatedKeys: {},
          offlineHandlerInProcess: false,
        }
      }
      if (data.index === undefined) {
        data.index = {}
      }

      if (!def.title) {
        def.title = {
          entityTitle: {
            entityNameTranslationKey: "SYSDefaultEntityName",
            tableDialogTitleTranslationKey: "SYSTableDialogTitle",
          },
        }
      }
      if (def.title.translationKey) {
        def.title.show = true
      }
      if (def.title.show === undefined) {
        def.title.show = false
      }
      if (def.title.invalidFavoritDisplayMode === undefined) {
        def.title.invalidFavoritDisplayMode = "Mark"
      }
      if (!def.title.entityTitle) {
        def.title.entityTitle = {
          entityNameTranslationKey: "SYSDefaultEntityName",
          tableDialogTitleTranslationKey: "SYSTableDialogTitle",
        }
      }

      if (!def.title.entityTitle.entityNameTranslationKey) {
        def.title.entityTitle.entityNameTranslationKey = "SYSDefaultEntityName"
      }
      if (!def.title.entityTitle.tableDialogTitleTranslationKey) {
        def.title.entityTitle.tableDialogTitleTranslationKey =
          "SYSTableDialogTitle"
      }

      if (!def.database) {
        def.database = {}
      }
      if (def.database.dataIdField === undefined) {
        if (
          def.database.dbInsertMode &&
          def.database.dbInsertMode.indexOf("UDT") > -1
        ) {
          def.database.dataIdField = "Code"
        } else {
          def.database.dataIdField = Object.keys(def.columns)[0]
        }
      }
      if (def.database.dbInsertMode === undefined) {
        def.database.dbInsertMode = "Manual"
      }

      // if (def.database.disableConstraintViolationSnacks === undefined) {
      //   def.database.disableConstraintViolationSnacks = false
      // }

      // if (def.database.constraintViolationBehaviour === undefined) {
      //   def.database.constraintViolationBehaviour = "Cancel"
      // }

      if (def.database.useSchema === undefined) {
        def.database.useSchema = def.database.dbInsertMode !== "Manual"
      }

      if (!def.server) {
        def.server = { endpoint: "" }
      }
      if (!def.feature) {
        def.feature = <SYSFeature>def.id
      }
      if (ovl.getActions().app.features[def.feature]?.hooks?.[HookTableInit]) {
        await ovl.getActions().app.features[def.feature].hooks[HookTableInit]({
          tableDef: def,
          context: "before",
        })
      }

      if (def.options === undefined) {
        def.options = {}
      }
      if (def.uiState === undefined) {
        def.uiState = {}
      }
      let options = def.options

      if (options.feedback === undefined) {
        options.feedback = { level: undefined }
      }

      if (options.feedback.level === undefined) {
        if (SYSConfig.tableDefaults?.feedback?.level) {
          options.feedback.level = SYSConfig.tableDefaults?.feedback.level
        } else {
          options.feedback = { level: "0:none" }
        }
      }

      if (options.addedRowsPosition === undefined) {
        if (SYSConfig.tableDefaults?.addNewRowsPosition) {
          options.addedRowsPosition = SYSConfig.tableDefaults.addNewRowsPosition
        } else {
          options.addedRowsPosition = "bottom"
        }
      }
      // if (options.copyColumnsIgnore === undefined) {
      //   options.copyColumnsIgnore = {}
      // }
      if (options.nullHandling === undefined) {
        options.nullHandling = {
          defaultDisplayValue: "<null>",
          //nullValidatorToUse: "Mandatory",
        }
      }
      if (options.nullHandling.defaultDisplayValue === undefined) {
        options.nullHandling.defaultDisplayValue = "<null>"
      }

      // if (options.nullHandling.nullValidatorToUse === undefined) {
      //   options.nullHandling.nullValidatorToUse = "Mandatory"
      // }

      if (options.filterSortSettings === undefined) {
        options.filterSortSettings = {
          staticFilterKey: undefined,
          doNotPersist: false,
          showInTitle: true,
        }
      }
      if (options.filterSortSettings.showInTitle === undefined) {
        options.filterSortSettings.showInTitle = true
      }
      if (options.filterSortSettings.doNotPersist === undefined) {
        options.filterSortSettings.doNotPersist = false
      }

      if (options.customRowActions === undefined) {
        options.customRowActions = {}
      }
      //init group
      Object.keys(options.customRowActions).forEach((k) => {
        options.customRowActions[k].fnKey = k
        if (!options.customRowActions[k].visualGroup) {
          options.customRowActions[k].visualGroup = "ovld"
        }

        if (options.customRowActions[k].blocking === undefined) {
          // no blocking at all if not defined
          options.customRowActions[k].blocking = { group: "view" }
        }
        if (options.customRowActions[k].blocking.group === undefined) {
          options.customRowActions[k].blocking.group = "view"
        }

        if (options.customRowActions[k].process === undefined) {
          if (
            options.customRowActions[k].prompt?.type === "WhenMulti" ||
            options.customRowActions[k].whenMultiple ||
            options.customRowActions[k].handleFeedback
          ) {
            options.customRowActions[k].process = "SYSTableRowFnProcess"
          } else {
            options.customRowActions[k].process = "None"
          }
        }

        if (options.customRowActions[k].prompt === undefined) {
          options.customRowActions[k].prompt = {}
        }
        if (options.customRowActions[k].prompt.type === undefined) {
          options.customRowActions[k].prompt.type = "None"
        }
        if (options.customRowActions[k].trackData === undefined) {
          options.customRowActions[k].trackData = "None"
        }

        if (options.customRowActions[k].summary === undefined) {
          options.customRowActions[k].summary = "Snack"
        }
      })

      if (options.customColumnActions === undefined) {
        options.customColumnActions = {}
      }
      //init group
      Object.keys(options.customColumnActions).forEach((k) => {
        options.customColumnActions[k].forEach((kk) => {
          if (!kk.visualGroup) {
            kk.visualGroup = "ovld"
          }
        })
      })

      if (options.navType === undefined) {
        options.navType = "bottom"
      }
      if (options.initial_filter === undefined) {
        options.initial_filter = { showSelected: false, value: "", static: {} }
      }
      if (options.initial_filter.value === undefined) {
        options.initial_filter.value = ""
      }
      if (options.initial_filter.trimValue === undefined) {
        options.initial_filter.trimValue =
          !!SYSConfig.tableDefaults?.trimTableFilterValue
      }

      if (options.initial_filter.static === undefined) {
        options.initial_filter.static = {}
      }
      if (options.initial_sort === undefined) {
        options.initial_sort = {
          direction: "desc",
          field: def.database.dataIdField,
          useValue: false,
        }
      }
      if (options.initial_sort.useValue === undefined) {
        options.initial_sort.useValue = false
      }

      if (options.initial_paging === undefined) {
        options.initial_paging = { page: 0, pageSize: 10000 }
      }
      if (options.edit === undefined) {
        options.edit = { editType: "big" }
      }

      if (options.edit.editType === undefined) {
        options.edit.editType = "big"
      }
      if (options.edit.groups === undefined) {
        options.edit.groups = {}
      }
      if (options.edit.displayValueHintWhileTyping === undefined) {
        options.edit.displayValueHintWhileTyping = false
      }

      if (options.edit.doFormChangedForEveryFieldInInit === undefined) {
        options.edit.doFormChangedForEveryFieldInInit = true
      }

      if (options.initial_sortCustom === undefined) {
        options.initial_sortCustom = { sorts: {}, selected: "" }
      }
      if (options.initial_sortCustom.sorts === undefined) {
        options.initial_sortCustom.sorts = {}
      }
      //init group
      Object.keys(options.initial_sortCustom.sorts).forEach((k) => {
        if (!options.initial_sortCustom.sorts[k].visualGroup) {
          options.initial_sortCustom.sorts[k].visualGroup = "ovld"
        }
      })

      if (options.initial_filterCustom === undefined) {
        options.initial_filterCustom = {}
      }
      //init group
      Object.keys(options.initial_filterCustom).forEach((k) => {
        if (!options.initial_filterCustom[k].visualGroup) {
          options.initial_filterCustom[k].visualGroup = "ovld"
        }
      })

      if (options.initial_rowAttributes === undefined) {
        options.initial_rowAttributes = {}
      }

      Object.keys(options.initial_rowAttributes).forEach((k) => {
        if (options.initial_rowAttributes[k].attributes === undefined) {
          options.initial_rowAttributes[k].attributes = {}
        }
      })

      if (options.controlsRendering === undefined) {
        options.controlsRendering = {
          checkbox: {
            view: { checked: "✔️", unchecked: "☐" },
            table: { checked: "✔️", unchecked: " " },
          },
        }
      }
      if (options.controlsRendering.useAlignRight === undefined) {
        options.controlsRendering.useAlignRight =
          options.edit.editType === "inline"
      }

      if (options.controlsRendering.fillSpaceIfNotVisible === undefined) {
        options.controlsRendering.fillSpaceIfNotVisible = true
      }
      if (options.controlsRendering.checkbox === undefined) {
        options.controlsRendering.checkbox = {
          view: { checked: "✔️", unchecked: "☐" },
          table: { checked: "✔️", unchecked: " " },
        }
      }
      if (options.controlsRendering.checkbox.view === undefined) {
        options.controlsRendering.checkbox.view = {
          checked: "??",
          unchecked: "?",
        }
      }
      if (options.controlsRendering.checkbox.table === undefined) {
        options.controlsRendering.checkbox.table = {
          checked: "??",
          unchecked: "",
        }
      }

      if (options.controlsRendering.fillSpaceIfNotVisible === undefined) {
        options.controlsRendering.fillSpaceIfNotVisible = true
      }

      if (options.controlsRendering.list === undefined) {
        options.controlsRendering.list = { displayCachedListsWhileTyping: true }
      }

      let initialSort: Sort
      let sortDef = def.options.initial_sort
      if (isMobile && sortDef.fieldMobile) {
        initialSort = {
          useValue: sortDef.useValue,
          direction: sortDef.directionMobile,
          field: sortDef.fieldMobile,
        }
      } else {
        initialSort = {
          direction: sortDef.direction,
          field: sortDef.field,
          useValue: sortDef.useValue,
        }
      }

      if (def.uiState.currentSelectedFilterSort === undefined) {
        def.uiState.currentSelectedFilterSort = ""
      }
      if (def.uiState.currentSelectedFilterSortStore === undefined) {
        def.uiState.currentSelectedFilterSortStore = {}
      }

      if (def.uiState.sort === undefined) {
        def.uiState.sort = {}
      }
      def.uiState.sort.sort = initialSort

      //def.uiState.sort.sort  = JSON.parse(JSON.stringify(def.options.initial_sort))
      def.uiState.sort.sortCustom = JSON.parse(
        JSON.stringify(def.options.initial_sortCustom)
      )

      def.uiState.paging = JSON.parse(
        JSON.stringify(def.options.initial_paging)
      )
      if (def.uiState.filter === undefined) {
        def.uiState.filter = {}
      }
      if (def.uiState.filter.columns === undefined) {
        def.uiState.filter.columns = {}
      }

      def.uiState.filter.filter = JSON.parse(
        JSON.stringify(def.options.initial_filter)
      )

      if (def.uiState._lastFilterSortDifferentiator === undefined) {
        def.uiState._lastFilterSortDifferentiator = "@@OvlInitValue@@Ovl"
      }

      if (!def.uiState.filter.filterCustom) {
        def.uiState.filter.filterCustom = {}
      }
      Object.keys(def.options.initial_filterCustom).forEach((f) => {
        def.uiState.filter.filterCustom[f] = {
          active: def.options.initial_filterCustom[f].active,
        }
      })

      //if (def.options.rowAttributes !== undefined) {
      def.uiState.filter.filterAttributes = {}
      Object.keys(def.options.initial_rowAttributes).forEach((f) => {
        def.uiState.filter.filterAttributes[f] = {
          attributes: JSON.parse(
            JSON.stringify(def.options.initial_rowAttributes[f].attributes)
          ),
        }
      })
      // console.log(
      //   JSON.parse(JSON.stringify(def.uiState.filter.filterAttributes))
      // )
      //}
      // def.uiState.filter.filterCustom = JSON.parse(
      //   JSON.stringify(def.options.initial_filterCustom)
      // )
      let uiState = def.uiState

      // if (uiState.viewRow === undefined) {
      //   uiState.viewRow = {}
      // }

      if (uiState.headerSelected === undefined) {
        uiState.headerSelected = ""
      }

      if (uiState.currentlyAddingKey === undefined) {
        //uiState.currentlyAddingKey = ""
      }

      if (uiState.dataFilteredAndSorted === undefined) {
        uiState.dataFilteredAndSorted = []
      }

      if (uiState.selectedCounter === undefined) {
        uiState.selectedCounter = 0
      }

      if (uiState.selectedRow === undefined) {
        uiState.selectedRow = {}
      }

      if (uiState.rebuild === undefined) {
        uiState.rebuild = {
          deletedKeys: {},
          updatedKeys: {},
          addedKeys: {},
          changed: {},
          needsRefresh: false,
        }
      }

      if (uiState.rowsCount === undefined) {
        uiState.rowsCount = 0
      }

      if (def.features === undefined) {
        def.features = {}
      }
      let features = def.features
      if (features.focusToFirstEditableField === undefined) {
        features.focusToFirstEditableField = true
      }

      if (features.detailView === undefined) {
        features.detailView = "None"
      }

      if (features.add === undefined) {
        features.add =
          features.multiCopy !== undefined ? features.multiCopy : false
      }

      if (features.delete === undefined) {
        features.delete =
          features.multiDelete !== undefined ? features.multiDelete : false
      }
      if (features.edit === undefined) {
        features.edit =
          features.multiEdit !== undefined ? features.multiEdit : false
      }

      if (features.copy === undefined) {
        features.copy =
          features.multiCopy !== undefined ? features.multiCopy : false
      }

      if (features.filter === undefined) {
        features.filter = false
      }

      if (isMobile()) {
        def.options.edit.editType = "big"
      }

      if (features.multiCopy === undefined) {
        features.multiCopy = false
      }
      if (features.multiCopy && def.options.edit.editType !== "inline") {
        features.multiCopy = false
      }
      if (features.multiEdit === undefined) {
        features.multiEdit = false
      }
      if (features.multiEdit && def.options.edit.editType !== "inline") {
        features.multiEdit = false
      }

      if (features.multiDelete === undefined) {
        features.multiDelete = false
      }

      if (features.multiFilter === undefined) {
        features.multiFilter = false
      }

      if (options.filterSortSettings.selectionClipping === undefined) {
        if (features.multiFilter) {
          options.filterSortSettings.selectionClipping = "Keep"
        } else {
          options.filterSortSettings.selectionClipping = "Remove"
        }
      }

      if (features.multiSelect === undefined) {
        features.multiSelect =
          features.multiCopy ||
          features.multiEdit ||
          features.multiDelete ||
          features.multiFilter ||
          Object.keys(def.options.customRowActions).some(
            (k) => def.options.customRowActions[k].whenMultiple
          )
      }
      if (!features.multiSelect) {
        features.displayMultiSelectEndSelectButton = "None"
      }
      if (features.displayMultiSelectEndSelectButton === undefined) {
        features.displayMultiSelectEndSelectButton = "TouchDevice"
      }
      if (features.stickyHeader === undefined) {
        features.stickyHeader = true
      }

      if (features.page === undefined) {
        features.page = false
      }
      if (features.showFooterButtons === undefined) {
        features.showFooterButtons = false
      }
      if (features.showRefreshButton === undefined) {
        features.showRefreshButton = "TableNav"
      }

      if (features.forceFreshServerDataIfOlderThan === undefined) {
        features.forceFreshServerDataIfOlderThan = -1
      }

      // if you need to adjust this delete then pls use tableinit in after mode
      if (features.delete) {
        let customRowActions = def.options.customRowActions
        customRowActions["OvlDelete"] = {
          permission: def.permission,
          permissionModifier: "D",
          icon: "sap-icon--delete",
          translationKey: "SYSTableRowFnDelete",
          visualGroup: "ovld",
          fnKey: "OvlDelete",
          trackData: "None",
          process: "SYSTableRowFnProcess",
          prompt: { type: "Always" },
          handleFeedback: true,
          blocking: { group: "edit" },
          summary: "Snack",
          cancelable: true,
        }
        if (features.multiDelete) {
          customRowActions["OvlDelete"].whenMultiple = {
            callForEveryRow: true,
          }
        }
      }

      if (features.copy) {
        let customRowActions = def.options.customRowActions
        customRowActions["OvlCopy"] = {
          permission: def.permission,
          permissionModifier: "A",
          icon: "sap-icon--copy",
          translationKey: "SYSTableRowFnCopy",
          visualGroup: "ovld",
          fnKey: "OvlCopy",
          blocking: { group: "view" },
          prompt: { type: "WhenMultiOrHookResult" },
          handleFeedback: true,
          process: "None",
          summary: "None",
        }
        if (features.multiCopy) {
          customRowActions["OvlCopy"].whenMultiple = {
            callForEveryRow: true,
          }
        }
      }

      if (features.edit) {
        let customRowActions = def.options.customRowActions
        customRowActions["OvlEdit"] = {
          permission: def.permission,
          permissionModifier: "M",
          icon: "sap-icon--edit",
          translationKey: "SYSTableRowFnEdit",
          visualGroup: "ovld",
          fnKey: "OvlEdit",
          prompt: { type: "WhenMultiOrHookResult" },
          handleFeedback: true,
          blocking: { group: "edit" },
          process: "None",
          summary: "None",
        }
        if (features.multiEdit) {
          customRowActions["OvlEdit"].whenMultiple = {
            callForEveryRow: true,
          }
        }
      }

      let customRowActions = def.options.customRowActions
      customRowActions["OvlSaveRow"] = {
        mode: "Hidden",
        permission: def.permission,
        permissionModifier: "M",
        translationKey: "SYSTableRowFnSave",
        fnKey: "OvlSaveRow",
        prompt: { type: "None" },
        handleFeedback: true,
        blocking: { group: "edit" },
        process: "SYSSaveRow",
      }

      Object.keys(def.options.customRowActions).forEach((k) => {
        // disable already all fns which are not accesible due of role or feature config
        // check roles and permissions as well
        let custom = def.options.customRowActions[k]
        let hidden =
          !(
            !custom.permission ||
            _HasPermission(custom.permission, custom.permissionModifier)
          ) &&
          (!custom.role || !!FeatureInfo(custom.role))
        custom.mode = hidden
          ? "Hidden"
          : def.options.customRowActions[k]?.mode === undefined
            ? "Row"
            : def.options.customRowActions[k].mode
        if (custom.visibleOnColumns === undefined) {
          custom.visibleOnColumns = "All"
        }
        if (custom.cancelable === undefined) {
          custom.cancelable = false
        }
      })

      // if add is explicitly enabled but no headermenu or footerbuutons enable...just enable footerbuttons

      if (!def.dataFetching) {
        def.dataFetching = {}
      }

      let dataFetching = def.dataFetching

      if (dataFetching.useCustomDataFetching === undefined) {
        dataFetching.useCustomDataFetching = false
      }

      // set some values if we are on mobile
    }

    def.uiState.tableGroupHeaderBreaks = {}
    def.uiState.tableGroupFooterBreaks = {}
    def.uiState.tableGroupResults = {}
    def.uiState.hasGroupedHeader = false
    def.uiState.groupHeaderFooterResultToSet = undefined
    let options = def.options

    if (options.tableGroupAggregates === undefined) {
      options.tableGroupAggregates = {
        aggregates: [],
        groups: [],
        disabled: true,
      }
    }

    if (options.tableGroupAggregates.aggregates === undefined) {
      options.tableGroupAggregates.aggregates = []
    }

    if (options.tableGroupAggregates.groups === undefined) {
      options.tableGroupAggregates.groups = []
    }

    if (options.tableGroupAggregates.disabled === undefined) {
      if (
        options.tableGroupAggregates.groups.length > 0 ||
        options.tableGroupAggregates.aggregates.length > 0
      ) {
        options.tableGroupAggregates.disabled = false
      } else {
        options.tableGroupAggregates.disabled = true
      }
    }

    if (
      options.tableGroupAggregates.aggregates.filter(
        (f) => f.aggregateCode === "@@SYSTotal"
      ).length === 0
    ) {
      options.tableGroupAggregates.aggregates.unshift({
        aggregateCode: "@@SYSTotal",
      })
    }
    if (
      options.tableGroupAggregates.groups.filter(
        (f) => f.groupCode === "@@SYSTotal"
      ).length === 0
    ) {
      options.tableGroupAggregates.groups.unshift({ groupCode: "@@SYSTotal" })
    }
    if (
      options.tableGroupAggregates.onlyApplyWhenColumnSortStartsWith ===
      undefined
    ) {
      options.tableGroupAggregates.onlyApplyWhenColumnSortStartsWith = []
    }
    if (options.tableGroupAggregates.onlyApplyWhenCustomSort === undefined) {
      options.tableGroupAggregates.onlyApplyWhenCustomSort = []
    }

    // update columns to always include idfield and sortfield if not there (as visible false)
    let columns = def.columns
    if (def.database.dataIdField && !def.columns[def.database.dataIdField]) {
      columns[def.database.dataIdField] = {
        ui: { visibility: "None" },
        sortable: true,
      }
    }
    if (
      def.uiState.sort.sort.field &&
      !def.columns[def.uiState.sort.sort.field]
    ) {
      columns[def.uiState.sort.sort.field] = {
        ui: { visibility: "None" },
        sortable: true,
      }
    }
    if (def.uiState.columnsRenderingState === undefined) {
      def.uiState.columnsRenderingState = { columns: {}, visibleInTable: [] }
    }

    let hasEditableColumns = false
    let order = 0
    Object.keys(def.columns).forEach((f) => {
      let col = def.columns[f]
      if (col.order === undefined) {
        col.order = order
      } else {
        order = col.order
      }
      order++
      if (col.ui === undefined) {
        col.ui = {}
      }
      if (col.ui.thGroupTranslationKey) {
        def.uiState.hasGroupedHeader = true
      }

      if (col.groups === undefined) {
        col.groups = { group: 0, tab: 0 }
      }

      if (col.groups.tab === undefined) {
        col.groups.tab = 0
      }
      if (col.groups.group === undefined) {
        col.groups.group = 0
      }
      if (col.ui.visibility === undefined) {
        col.ui.visibility = "Table_Edit_View"
      }
      if (col.ui.fillSpaceIfNotVisible === undefined) {
        col.ui.fillSpaceIfNotVisible =
          def.options.controlsRendering.fillSpaceIfNotVisible
      }

      if (col.ui.language) {
        if (col.ui.translationVisibility === undefined) {
          col.ui.translationVisibility = "Edit"
        }
      }

      if (
        col.ui.selectIfFocused === undefined &&
        options.controlsRendering.selectIfFocused !== undefined
      ) {
        col.ui.selectIfFocused = options.controlsRendering.selectIfFocused
      }

      // if (col.ui.showLabelIfNoValueInView === undefined) {
      //   col.ui.showLabelIfNoValueInView = true
      // }
      if (col.nullHandling === undefined) {
        col.nullHandling = {
          enabled: false,
          displayValue: def.options.nullHandling.defaultDisplayValue,
          setToNullIf: data.clientConversion?.[f]?.setToNullIf,
          setToValueIfNull: data.clientConversion?.[f]?.setToValueIfNull,
        }
      }
      if (col.nullHandling.displayValue === undefined) {
        col.nullHandling.displayValue =
          def.options.nullHandling.defaultDisplayValue
      }
      if (col.nullHandling.enabled === undefined) {
        col.nullHandling.enabled = false
      }
      _controlInit(col)

      if (col.ui.checkedValue === "Y" && col.ui.uncheckedValue === undefined) {
        col.ui.uncheckedValue = "N"
      }
      if (col.filter === undefined) {
        col.filter = {}
      }
      if (col.filter.columnFilter) {
        if (col.filter.columnFilter.list === undefined) {
          col.filter.columnFilter.list = []
        }
        if (!col.filter.columnFilter.exclusive) {
          col.filter.columnFilter.exclusive = false
        }
      }

      if (col.list) {
        if (col.list.displayField === undefined && col.list.valueField) {
          col.list.displayField = col.list.valueField
        }

        if (
          col.list.displayValueField === undefined &&
          col.list.valueField === col.list.displayField
        ) {
          col.list.displayValueField = false
        }
      }
      def.uiState.filter.columns[f] = JSON.parse(JSON.stringify(col.filter))

      // if schema is used the align will be done in enrichColumn after TableServerRefresh
      if (col.ui.align === undefined && (!def.database.useSchema || col.type)) {
        if (!col.list && (col.type === "decimal" || col.type === "int")) {
          col.ui.align = "right"
        } else {
          col.ui.align = "left"
        }
      }
      // if (col.type === undefined) {
      //   col.type = "text"
      // }
      if (
        def.features.headerMenu === undefined &&
        (col.sortable || col.filter.rangeFilter || col.filter.columnFilter)
      ) {
        def.features.headerMenu = true
      }
      if (!hasEditableColumns && col.ui.visibility.indexOf("Edit") > -1) {
        hasEditableColumns = true
      }
      def.uiState.columnsRenderingState.columns[f] = {
        visibility: col.ui.visibility,
        alignCss: _GetAlign(col.ui.align),
      }
    })
    def.uiState.hasEditableColumns = hasEditableColumns
    if (
      def.features.headerMenu === undefined &&
      (Object.keys(def.options.initial_filterCustom).length > 0 ||
        Object.keys(def.options.initial_sortCustom).length > 0)
    ) {
      def.features.headerMenu = true
    }

    if (def.features.add && !def.features.headerMenu) {
      def.features.showFooterButtons = true
    }
    if (!wasInitialised) {
      if (ovl.getActions().app.features[def.feature]?.hooks?.[HookTableInit]) {
        await ovl.getActions().app.features[def.feature].hooks[HookTableInit]({
          tableDef: def,
          context: "after",
        })
      }
    }
  }

  await _TableSetColumnVisibility(def, ovl.getActions(), ovl.getState(), true)
}

export const _TableRefreshServerData = async (
  def: SYSTableDef,
  data: SYSTableData,
  actions: SYSActions,
  forceServerDataRefresh?: boolean,
  localData?: any[],
  serverFilter?: {},
  localDataSync?: _LocalDataSyncType
) => {
  if (!data.timestamp || localData || !!forceServerDataRefresh) {
    // now if there is no data do a get request

    await actions.ovl.table._TableRefreshDataFromServer({
      def,
      data,
      localData,
      serverFilter,
      localDataSync,
    })
  }
}

export const _EnrichColumnsFromSchema = (
  data: SYSTableData,
  def: SYSTableDef
) => {
  let schema = data.schema
  if (
    !def.columnsInitialisedFromSchema &&
    def.database.useSchema &&
    schema !== undefined
  ) {
    def.columnsInitialisedFromSchema = true
    Object.keys(def.columns).forEach((f) => {
      let col = def.columns[f]
      if (!col.type && schema?.[f]) {
        col.type = schema[f].type
      }
      if (!col.ui?.align) {
        let align: _ColumnAlign =
          !col.list && (col.type === "decimal" || col.type === "int")
            ? "right"
            : "left"
        if (!col.ui) {
          col.ui = { align }
        } else {
          col.ui.align = align
        }
      }
    })
  }
}

export const TableSortFn = (def: SYSTableDef, restable: any[], data: any) => {
  let validSortGroupsFn = []
  let fn = ovl.getActions().app.features[def.feature]
  if (fn?.hooks) {
    def.options.tableGroupAggregates.groups
      .filter((f) => f.respectInSort === "ForceGroup")
      .forEach((group) => {
        let fnName = HookTableGroupSortValue.replace(
          "{groupKey}",
          group.groupCode
        )
        if (!fn.hooks[fnName]) {
          fnName = HookTableGroupValue.replace("{groupKey}", group.groupCode)
        }
        if (fn.hooks[fnName]) {
          validSortGroupsFn.push(fn.hooks[fnName])
        }
      })
  }
  const sortfield = def.uiState.sort.sort.field
  let type
  let isList: boolean
  let colDef
  if (sortfield) {
    type = def.columns[sortfield].type
    isList = def.columns[sortfield].list !== undefined
    colDef = def.columns[sortfield]
  }
  let lang = ovl.getState().ovl.session.Language

  let customSortFn = undefined
  let sortCustom = def.uiState.sort.sortCustom

  if (sortCustom.selected && sortCustom.sorts[sortCustom.selected]) {
    let functionName = HookTableCustomSort.replace(
      "{sortKey}",
      sortCustom.selected
    )
    if (fn?.hooks?.[functionName]) {
      customSortFn = fn.hooks[functionName]
    } else {
      throw new Error(
        "ovl customSort function: " +
          functionName +
          " in customFunctions not found!"
      )
    }
  }

  let renderSortfieldValueFn = undefined

  if (sortfield && fn?.hooks) {
    let fnName = HookFieldGetValueRender.replace("{subFeature}", "").replace(
      "{fieldKey}",
      sortfield
    )
    if (fn?.hooks?.[fnName]) {
      renderSortfieldValueFn = fn.hooks[fnName]
    }
  }

  const ascending = def.uiState.sort.sort.direction === "asc" ? 1 : -1
  const useValue = def.uiState.sort.sort.useValue
  let sortByFeedback = !!def.uiState.sort.byFeedback
  let res: number = 0
  return restable.sort((a, b) => {
    for (let fn of validSortGroupsFn) {
      let groupValA = fn({
        def,
        data,
        row: data[a],
      })
      let groupValB = fn({
        def,
        data,
        row: data[b],
      })
      res = SortGet(groupValA, groupValB)
      if (res !== 0) {
        return res
      }
    }
    if (sortByFeedback) {
      res =
        data[b].OvlSysState.lastFeedbackTS - data[a].OvlSysState.lastFeedbackTS
      if (res !== 0) {
        return res
      }
    }
    if (customSortFn !== undefined) {
      return <HookTableCustomSort_ReturnType>customSortFn(<
        HookTableCustomSort_Type
      >{
        def,
        lang,
        a,
        b,
        data /*if we have other logic than "ForceGroup" submit the groupRes here as param -> , groupRes*/,
      })
    }
    if (sortfield) {
      let valB
      let valA
      if (!renderSortfieldValueFn) {
        valA = data[a][sortfield]
        valB = data[b][sortfield]
      } else {
        let renderVal: HookFieldGetValueRender_ReturnType
        renderVal = renderSortfieldValueFn(<HookFieldGetValueRender_Type>{
          tableDef: def,
          rowKey: a,
          row: data[a],
          columnKey: sortfield,
          displayMode: "Table",
        })
        if (!renderVal) {
          valA = data[a][sortfield]
        } else {
          valA =
            renderVal.valueRaw !== undefined
              ? renderVal.valueRaw
              : renderVal.res
        }
        renderVal = <HookFieldGetValueRender_ReturnType>renderSortfieldValueFn(<
          HookFieldGetValueRender_Type
        >{
          tableDef: def,
          rowKey: b,
          row: data[b],
          columnKey: sortfield,
          displayMode: "Table",
        })
        if (!renderVal) {
          valB = data[b][sortfield]
        } else {
          valB =
            renderVal.valueRaw !== undefined
              ? renderVal.valueRaw
              : renderVal.res
        }
      }

      if (isList && !useValue) {
        valB = getDisplayValue(
          sortfield,
          colDef,
          valB,
          def.feature,
          undefined,
          def.id
        )
        valA = getDisplayValue(
          sortfield,
          colDef,
          valA,
          def.feature,
          undefined,
          def.id
        )
      }
      res = SortGet(valA, valB)
      return res * ascending
    }
  })
}

export const _TableFilterFn = (
  tableDataAndDef: TableDataAndDef,
  calledFromColumnFilterId?: string
): string[] => {
  // filter should only operate on rows not in add-mode
  let def = tableDataAndDef.def
  let lang = ovl.getState().ovl.session.Language
  let data = tableDataAndDef.data.data
  let columns = def.uiState.filter.columns
  let originalColumns = def.columns
  let restable: string[]
  restable = Object.keys(data)

  // new 01.07.2024, do not filter out add mode. because we habve autoRebuild feature which would make this rows in edit/add disappear
  // let restable = Object.keys(data).filter(
  //   (f) => tableDataAndDef.data.rowState[f].state !== "Add"
  // )
  const staticFilter = def.uiState.filter.filter.static
  // make sure that
  // 1. staticFilter gets applied (for master - details scenarios)
  // 2. temp rows in add mode get filtered out (they are handled in addRwos array)
  // 3. rows which point to null gets filtered out (eg. delete => sets em to null)

  let filterKeys = Object.keys(staticFilter)
  filterKeys.forEach((f) => {
    restable = restable.filter((v) => {
      let rowState = (data[v].OvlSysState as OvlSysColumn).rowState
      if (rowState && rowState.lastChangeBelongsToTableDefId) {
        // now if staticfilter still matches
        // then it still belongs to selection. else delete it from selection
        if (
          filterKeys.filter((f2) => staticFilter[f2] === data[v][f2]).length !==
          filterKeys.length
        ) {
          if (def.uiState.selectedRow[v]) {
            def.uiState.selectedRow[v].selected = false
            def.uiState.selectedRow[v].showNav = false
          }
        }
        rowState.lastChangeBelongsToTableDefId = undefined
      }
      return data[v][f] === staticFilter[f]
    })
  })
  let filterCustom = def.uiState.filter.filterCustom
  let customFilter = Object.keys(filterCustom).filter(
    (k) => filterCustom[k].active
  )
  let customFilterFn = customFilter.reduce((val, k) => {
    let functionName = HookTableCustomFilter.replace("{filterKey}", k)
    let fn = ovl.getActions().app.features[def.feature]
    if (fn?.hooks?.[functionName]) {
      val.push(fn.hooks[functionName])
      return val
    } else {
      throw new Error(
        "ovl customFilter function: " + functionName + " not found!"
      )
    }
  }, [])
  let hasCustomFilter = customFilterFn.length > 0
  let selectedRow = def.uiState.selectedRow

  let visibleColumns = Object.keys(def.columns).filter(
    (f) => def.columns[f].ui.visibility.indexOf("Table") > -1
  )

  let columnFilterColumns = Object.keys(def.uiState.filter.columns).filter(
    (k) =>
      columns[k].columnFilter &&
      columns[k].columnFilter.type !== "none" &&
      k !== calledFromColumnFilterId
  )
  let hasColumnFilter = columnFilterColumns.length > 0
  let rangeFilterColumns = Object.keys(def.uiState.filter.columns).filter(
    (s) => columns[s].rangeFilter && columns[s].rangeFilter.selected !== "none"
  )
  let hasRangeFilters = rangeFilterColumns.length > 0

  // restable is now filtered by static filter already, thats fine and our starting point for the next 4 filters

  // 1. selectedFilter = just ignore other filters
  // 2. columnFilters
  // 3. customFilters
  // 4. attributeFilter
  // 5. tableFilter

  // 1st Selected filter is easy. When activated just return the selected rows

  if (
    calledFromColumnFilterId === undefined &&
    def.uiState.filter.filter.showSelected
  ) {
    return Object.keys(selectedRow).filter((k) => selectedRow[k].selected)
  }

  // now the rangefilters
  if (hasRangeFilters) {
    restable = restable.filter((rowKey) => {
      // now go column by column
      return (
        rangeFilterColumns.filter((columnId) => {
          let column = columns[columnId]
          let row = data[rowKey]
          let val = column.rangeFilter.val
          let val2 = column.rangeFilter.val2
          let valToCompare = GetRowRawValue(def, columnId, data[rowKey], rowKey)
          if (originalColumns[columnId].type === "date") {
            valToCompare = `${getOnlyDateISOString(
              new Date(valToCompare)
            )}T00:00:00`
          }
          switch (column.rangeFilter.selected) {
            case "equal":
              return val === valToCompare
            case "greaterThanOrEqual":
              return valToCompare >= val && (!val2 || valToCompare <= val2)
            case "lessThanOrEqual":
              return valToCompare <= val
          }
        }).length === rangeFilterColumns.length
      )
    })
  }

  // now do the columnfilters
  if (hasColumnFilter) {
    restable = restable.filter((rowKey) => {
      let res = columnFilterColumns.filter((columnId) => {
        let column = columns[columnId]
        let filter = column.columnFilter

        let nullHandling = def.columns[columnId].nullHandling.enabled
        let val
        // if its caluclated we need to get caluclatedvalue as well here
        //        if (def.columns[columnId].schema?.isCalculatedColumn) {
        val = GetRowRawValue(def, columnId, data[rowKey], rowKey)
        // } else {
        //   val = data[rowKey][columnId]
        // }
        // there can be multiple filters so if one fits its valid (=>some())
        let res = filter.list.some((s) => {
          return val === s || (!nullHandling && s === "" && val === null)
        })
        if (filter.exclusive) {
          res = !res
        }
        return res
      })
      return res.length === columnFilterColumns.length
    })
  }

  // now the customFilters
  if (hasCustomFilter) {
    let data = tableDataAndDef.data
    restable = restable.filter((rowKey) => {
      let row = data.data[rowKey]
      return !customFilterFn.some(
        (f) => !f({ def, lang, data, row } as HookTableCustomFilter_Type)
      )
    })
  }
  //console.log(JSON.parse(JSON.stringify(def.uiState.filter.filterAttributes)))
  let filterAttributes = def.uiState.filter.filterAttributes
  if (filterAttributes) {
    let getAttributesFn =
      ovl.getActions().app.features[def.feature]?.hooks?.[
        HookTableRowAttributesGetAttributes
      ]
    if (getAttributesFn) {
      let data = tableDataAndDef.data
      let attributeDefKeys = Object.keys(filterAttributes)
      attributeDefKeys.forEach((defKey) => {
        restable = restable.filter((rowKey) => {
          let row = data.data[rowKey]
          let attributesToCheckFor = Object.keys(
            filterAttributes[defKey].attributes
          )
          let cnt = attributesToCheckFor.length
          if (cnt === 0) {
            return true
          }
          let rowAttributes = getAttributesFn({
            defId: def.id,
            row,
            rowAttributeDefKey: defKey,
          } as HookTableRowAttributesGetAttributes_Type)
          if (rowAttributes === undefined) {
            return false
          }
          let isArray = Array.isArray(rowAttributes)
          return (
            attributesToCheckFor.filter((attributeKey) => {
              if (isArray) {
                return rowAttributes.indexOf(attributeKey) > -1
              } else {
                return rowAttributes[attributeKey]
              }
            }).length === cnt
          )
        })
      })
    }
  }

  // if tablefiltering is switched off thats it, just return
  if (!def.features.filter) {
    return restable
  }
  let hasTableFilter = def.uiState.filter.filter.value !== ""
  //and the tableFilter which checks all the columns
  if (hasTableFilter) {
    let renderValueFns = {}
    let fn = ovl.getActions().app.features[def.feature]?.hooks
    if (fn) {
      visibleColumns.forEach((columnKey) => {
        let fnName = HookFieldGetValueRender.replace(
          "{subFeature}",
          ""
        ).replace("{fieldKey}", columnKey)
        if (fn[fnName]) {
          renderValueFns[columnKey] = fn[fnName]
        }
      })
    }

    restable = restable.filter((rowKey) => {
      return visibleColumns.some((columnId) => {
        let column = def.columns[columnId]
        let dispValue
        if (renderValueFns[columnId]) {
          let renderVal: HookFieldGetValueRender_ReturnType = renderValueFns[
            columnId
          ](<HookFieldGetValueRender_Type>{
            tableDef: def,
            rowKey,
            row: data[rowKey],
            columnKey: columnId,
            displayMode: "Table",
          })
          dispValue = renderVal.valueRaw ? renderVal.valueRaw : renderVal.res
        } else {
          dispValue = getDisplayValue(
            columnId,
            column,
            data[rowKey][columnId],
            def.feature,
            undefined,
            def.id
          )
        }

        if (dispValue === null || dispValue === undefined) {
          return false
        }
        return (
          dispValue
            .toString()
            .toLowerCase()
            .indexOf(def.uiState.filter.filter.value.toLowerCase()) > -1
        )
      })
    })
  }

  return restable
}

export const getFormFieldsFromColumns = (
  def: SYSTableDef,
  row,
  noLabel?: boolean
) => {
  // use this to initialise formfields when coming from table
  // if something is generic to a formfield put it in the other helper method which also will be used for custom forms
  let formFields: { [key: string]: SYSFormField } = {}
  let columns = def.columns
  Object.keys(columns).map((k) => {
    let col = columns[k]
    if (col.ui.visibility.indexOf("Edit") > -1) {
      let type: DataType = "text"
      if (col.control === "checkbox") {
        if (col.ui.checkedValue === undefined) {
          col.ui.checkedValue = true
        }
        if (col.type === undefined) {
          col.type = "bool"
        }
      }
      if (col.type) {
        type = col.type
      }
      formFields[k] = {
        type,
        value:
          type === "object" && row[k] !== null
            ? JSON.stringify(row[k])
            : row[k],
        nullHandling: {
          enabled: col.nullHandling.enabled,
          setToNullIf: col.nullHandling.setToNullIf,
          setToValueIfNull: col.nullHandling.setToValueIfNull,
        },
        list: col.list
          ? JSON.parse(JSON.stringify(col.list), stringifyReplacer)
          : undefined,
        ui: col.ui
          ? JSON.parse(JSON.stringify(col.ui), stringifyReplacer)
          : undefined,
        schema: col.schema ? JSON.parse(JSON.stringify(col.schema)) : undefined,
        groups: col.groups,

        editor: col.editor,
      }
      if (!formFields[k].ui) {
        formFields[k].ui = {}
      }
      formFields[k].ui.noLabel = col.ui?.noLabel === true ? true : !!noLabel
      let visible: _ColumnVisibility
      if (
        col.ui.language &&
        col.ui.language !== ovl.getState().ovl.session.Language
      ) {
        visible = col.ui.translationVisibility
      } else {
        visible = col.ui.visibility
      }
      if (visible && visible.indexOf("Edit") < 0) {
        formFields[k].inactive = true
        formFields[k].ui.visible = "false"
      }

      if (formFields[k].groups === undefined) {
        formFields[k].groups = { group: 0, tab: 0 }
      }

      if (formFields[k].groups.group === undefined) {
        formFields[k].groups.group = 0
      }

      if (formFields[k].groups.tab === undefined) {
        formFields[k].groups.tab = 0
      }

      if (col.control === "option") {
        if (!formFields[k].list) {
          throw new Error("ovl controltype 'control' needs list defined..")
        }
        formFields[k].list.mode = "Option"
      }
      if (col.type === "date" && col.ui.useBrowserDatePicker) {
        col.control = "date"
      }
      if (
        formFields[k].list &&
        formFields[k].list.showCachedHitsWhileTyping === undefined
      ) {
        formFields[k].list.showCachedHitsWhileTyping =
          def.options.controlsRendering.list.displayCachedListsWhileTyping
      }
      formFields[k].ui.displayValueHintWhileTyping =
        def.options.edit.displayValueHintWhileTyping

      formFields[k].control = col.control
      formFields[k].order = col.order
      def.options.edit.displayValueHintWhileTyping
    }
  })
  return formFields
}

export type _RowFnCanToSet = {
  [key: string]: {
    visibility?: FormFnCan_Visibility
    tooltip?: string
    icon?: string
    customClass?: string
  }
}

export const _GetRowFnToSet = (def: SYSTableDef): _RowFnCanToSet => {
  let customRowActions = def.options.customRowActions
  let res: any = Object.keys(customRowActions)
    .filter((f) => customRowActions[f].mode.indexOf("Row") > -1)
    .reduce((val, fnNameId) => {
      let custom = customRowActions[fnNameId]

      val[fnNameId] = {
        visibility: "Visible",
        icon: custom.icon,
        tooltip: T(custom.translationKey),
        customClass: undefined,
      }
      return val
    }, {})

  if (
    !Object.keys(def.columns).some(
      (k) => def.columns[k].ui.visibility.indexOf("Edit") > -1
    )
  ) {
    if (res.OvlCopy) {
      res.OvlCopy.visibility = "NotVisible"
    }
    if (res.OvlEdit) {
      res.OvlEdit.visibility = "NotVisible"
    }
  }

  return res
}

export const _DoRowFnProcessRunningCheck = (
  rowControlActions: _RowFnCanToSet,
  def: SYSTableDef,
  key: string,
  singleFnId: string
) => {
  let customRowActions = def.options.customRowActions
  let processState = ovl.getState().ovl.longRunningProcessTracker
  let hasEditBlock = false
  let hasViewBlock = false
  let alreadyDisabled = new Set<string>()
  Object.keys(customRowActions)
    .filter(
      (k) =>
        customRowActions[k].process !== "None" &&
        (!singleFnId || k === singleFnId)
    )
    .forEach((fnNameId) => {
      // get the process name out of def
      let process = <SYSLongRunningProcess>(
        def.options.customRowActions?.[fnNameId]?.process
      )
      // danger code: when changing check tracking with some rowfns and also in rehydrate mode
      // reason: checking if a process is running will check some state. this state is not guaranteed to be there thats why it will be cerated in ProcessIsRunning
      let instanceKey = `${def.id}_OvlSep_${fnNameId}_OvlSep_Multi`
      let isRunning = ProcessIsRunning(process, instanceKey)
      let instanceState = processState[process].instance?.[instanceKey]
      isRunning = !!instanceState.tableFn.lockedTableKeys[key] && isRunning
      if (isRunning) {
        if (!hasEditBlock) {
          hasEditBlock = customRowActions[fnNameId].blocking?.group === "edit"
        }
        if (!hasViewBlock) {
          hasViewBlock = customRowActions[fnNameId].blocking?.group === "view"
        }
      }
      if (!isRunning) {
        instanceKey = `${def.id}_OvlSep_${fnNameId}_OvlSep_${key}`
        isRunning = ProcessIsRunning(process, instanceKey)
        instanceState = processState[process].instance?.[instanceKey]
        isRunning = !!instanceState.tableFn.lockedTableKeys[key] && isRunning
        if (isRunning) {
          if (!hasEditBlock) {
            hasEditBlock = customRowActions[fnNameId].blocking?.group === "edit"
          }
          if (!hasViewBlock) {
            hasViewBlock = customRowActions[fnNameId].blocking?.group === "view"
          }
        }
      }
      if (isRunning) {
        if (rowControlActions[fnNameId]) {
          rowControlActions[fnNameId].visibility = "Disabled"
          alreadyDisabled.add(fnNameId)
        }
      }
    })
  if (hasEditBlock || hasViewBlock) {
    Object.keys(rowControlActions)
      .filter(
        (f) =>
          rowControlActions[f].visibility !== "NotVisible" &&
          (!singleFnId || f === singleFnId) &&
          !alreadyDisabled.has(f)
      )
      .forEach((fnNameId) => {
        if (hasEditBlock) {
          rowControlActions[fnNameId].visibility = "Disabled"
        } else if (
          hasViewBlock &&
          customRowActions[fnNameId].blocking.group === "edit"
        ) {
          rowControlActions[fnNameId].visibility = "Disabled"
        }
      })
  }
}

export const createDynamicRowFunctions = async (
  def: SYSTableDef,
  data: SYSTableData,
  key: string,
  isTableRowDetailView: boolean
): Promise<_RowFnCanToSet> => {
  let rowControlActions: _RowFnCanToSet = _GetRowFnToSet(def)
  // first all custom ones
  let comp = _RenderTracking.currentComponent
  // now check if an action is running and disable
  // we only need to do it for custom actions because the other ones are handled
  // but we will set the delete and edit,copy to disabled if one of the proc is running

  let fn = ovl.getActions().app.features[def.feature]
  let functionName = HookTableRowFnCan

  if (fn?.hooks?.[functionName]) {
    await fn.hooks[functionName]({
      _functionsToSet: rowControlActions,
      rowKey: key,
      tableDef: def,
      tableData: data,
      row: data.data[key],
      set: (fnKey: string, options: {}) => {
        _tableRowFnSetHelper(fnKey, options, rowControlActions)
      },
    } as HookTableRowFnCan_Type)
  }

  _RenderTracking.currentComponent = comp
  _RenderTracking.pauseTracking = false

  _DoRowFnProcessRunningCheck(rowControlActions, def, key, undefined)

  return rowControlActions
}

export const _tableRowFnSetHelper = (
  fnKey: string | string[],
  options: any,
  functionsToSet: {}
) => {
  if (!Array.isArray(fnKey)) {
    fnKey = [fnKey]
  }
  fnKey.forEach((key) => {
    if (functionsToSet[key]) {
      if (options.visibility) {
        if (
          options.visibility !== "Disabled" ||
          functionsToSet[key].visibility !== "NotVisible"
        ) {
          functionsToSet[key].visibility = options.visibility
        }
      }
      if (options.icon) {
        functionsToSet[key].icon = options.icon
      }
      if (options.tooltip) {
        functionsToSet[key].tooltip = options.tooltip
      }
      if (options.customClass) {
        functionsToSet[key].customClass = options.customClass
      }
    }
  })
}

export const _showTableRowFnConfirm = async (
  systemValidationContent: any,
  systemConfirmContent: any,
  customConfirmContent: any,
  title: any,
  connectedForm: { formType: string; formId: string }
): Promise<unknown> => {
  return DialogOkCancel(
    html`<div class="ovl-tablerowfnconfirm">
      <div class="__confirmsystemvalidation">${systemValidationContent}</div>
      <div class="__confirmcustom">${customConfirmContent}</div>
      <div class="__confirmsystemconfirm">${systemConfirmContent}</div>
    </div>`,
    { title, connectedForm }
  )
}

export let _GetFnResultToSet = (
  fnKey,
  process,
  data
): RowFunctionResultToSet => {
  return {
    setFromResult: async (res) => {
      let res2 = JSON.parse(JSON.stringify(res))
      let rowKey = data.index[res2.id]
      // if its an new data, we need the id lateron after we have created the row
      if (rowKey) {
        res2.id = rowKey
      }
      res2.fnId = fnKey
      await process.setFromResult(res2)
      return rowKey
    },
    setSuccess(
      id,
      text,
      code,
      cnt?: TableRowFnProcessRawFeedbackSummaryCount_Type
    ) {
      let rowKey = data.index[id]
      process.setSuccess(id, text, code, cnt)
      return rowKey
    },
    setInformation(
      id,
      text,
      code,
      cnt?: TableRowFnProcessRawFeedbackSummaryCount_Type
    ) {
      let rowKey = data.index[id]
      process.setInformation(id, text, code, cnt)
      return rowKey
    },
    setWarning(
      id,
      text,
      code,
      cnt?: TableRowFnProcessRawFeedbackSummaryCount_Type
    ) {
      let rowKey = data.index[id]
      process.setWarning(id, text, code, cnt)
      return rowKey
    },
    setError(
      id,
      text,
      code,
      cnt?: TableRowFnProcessRawFeedbackSummaryCount_Type
    ) {
      let rowKey = data.index[id]
      process.setError(id, text, code, cnt)
      return rowKey
    },
  }
}

export const rowControlActionsHandler = async (
  isCustom: boolean,

  key: string,
  def: SYSTableDef,
  data: SYSTableData,
  rowKey: string,
  isTableRowDetailView: boolean
  //formState?: SYSFormState
) => {
  //if (isCustom) {
  let customFns = ovl.getActions().app.features[def.feature]
  let fnResultToSet: RowFunctionResultToSet = {}

  let connectedFormBehaviour = {
    formType: undefined,
    formId: undefined,
    fieldsResetType: undefined,
  }
  if (customFns) {
    let customFunctionName = HookTableRowFn
    let customFunction = customFns.hooks[customFunctionName]
    let customFnDef = def.options.customRowActions[key]
    if (
      key === "OvlDelete" ||
      key === "OvlCopy" ||
      key === "OvlEdit" ||
      customFunction ||
      customFnDef?.endpoint?.url
    ) {
      let process: _ProcessReturnType
      let connectedConfirmFormState
      // if (isTableRowDetailView) {
      //   ovl.getActions().ovl.dialog._DialogClose("SYSTableRowDetailView")
      // }
      let trackStatusIntervall = { id: -1 }
      let clientId = ovl.getState().ovl.app.clientId
      if (customFnDef) {
        let systemConfirmContent = T("SYSTableRowFnConfirm", [
          T(customFnDef.translationKey),
          "",
        ])
        let systemValidationContent
        let customConfirmContent
        let fnName = T(customFnDef.translationKey)
        if (
          customFnDef?.prompt.type === "Always" ||
          customFnDef?.prompt.type === "WhenMultiOrHookResult"
        ) {
          let validKeys = [rowKey]
          let confirmRes = await _RowFnConfirmFormHelper(
            systemConfirmContent,
            systemValidationContent,
            customConfirmContent,
            key,
            connectedFormBehaviour,
            customFnDef,
            customFns,
            data,
            def,
            rowKey,
            fnName,
            validKeys,
            [rowKey],
            customFnDef?.prompt.type === "WhenMultiOrHookResult"
          )
          if (!confirmRes || validKeys.length === 0) {
            return
          }
        }

        if (customFnDef.process !== "None") {
          // we don't do snacks fro local deletes
          let snack: ProcessSnackType = { type: "none" }
          if (
            !(
              key === "OvlDelete" &&
              def.features.saveLocallyMode === "SaveLocally"
            )
          ) {
            snack = {
              text: T(customFnDef.process, [T(customFnDef.translationKey), ""]),
              type: "BusyIndicator",
            }
          }
          process = ProcessStart(customFnDef.process, {
            instance: `${def.id}_OvlSep_${key}_OvlSep_${rowKey}`,
            snack,
            tableFn: {
              fnId: key,
              tableDefId: def.id,
              fnName,
              lockedTableKeys: { [rowKey]: true },
              rowKey,
            },
          })
          fnResultToSet = _GetFnResultToSet(key, process, data)

          if (process && customFnDef.trackData !== "None") {
            _handleRowFnTracking(
              customFnDef.trackData,
              trackStatusIntervall,
              `${key}_${rowKey}`,
              clientId,
              process,
              fnResultToSet,
              fnName
            )
          }
        }

        if (connectedFormBehaviour?.formType) {
          connectedConfirmFormState = FormGet(
            <SYSForm>connectedFormBehaviour.formType,
            {
              instance: connectedFormBehaviour.formId,
            }
          )
        }
      }
      try {
        if (customFnDef?.endpoint?.url) {
          await _handleRowFnEndpointData(
            `${key}_${rowKey}`,
            clientId,
            customFnDef.endpoint.url,
            fnResultToSet,
            data,
            def,
            [rowKey],
            connectedConfirmFormState,
            process,
            undefined
          )
        } else {
          if (key === "OvlDelete") {
            await ovl.getActions().ovl.internal._TableDeleteRow({
              key: rowKey,
              def: def,
              data,
              process: process,
            })
          } else if (key === "OvlCopy") {
            let res = await ovl.getActions().ovl.internal._TableCopyRow({
              key: rowKey,
              def: def,
              data,
            })
            await ovl
              .getActions()
              .ovl.table._TableEditRow({ key: res.key, data, def })
          } else if (key === "OvlEdit") {
            await ovl
              .getActions()
              .ovl.table._TableEditRow({ key: rowKey, data, def })
          } else {
            await customFunction(<HookTableRowFn_Type>{
              fnKey: key,
              isSingle: true,
              rowKey,
              def,
              data,
              row: data.data[rowKey],
              rows: data.data,
              selectedKeys: [rowKey],
              resultToSet: fnResultToSet,
              connectedConfirmFormState,
            })
          }
        }
        if (
          !(
            key === "OvlDelete" &&
            def.features.saveLocallyMode === "SaveLocally"
          ) &&
          process
        ) {
          process.showSummary(<any>customFnDef.summary)
        }
        if (process) {
          _handleRowFnConfirmFieldsReset(
            connectedFormBehaviour,
            process?.currentFeedback
          )
          _DoRebuildAndFixPage(def)
        }
      } finally {
        if (process) {
          clearInterval(trackStatusIntervall.id)
          process.stop()
        }
      }
    } else if (isCustom) {
      throw Error(
        "SYS error: Custom Action: " + customFunctionName + " not found!"
      )
    }
  }
}
export const _RowFnConfirmFormHelper = async (
  systemConfirmContent,
  systemValidationContent,
  customConfirmContent,
  fnKey: string,
  connectedFormBehaviour,
  customFnDef,
  customFns,
  data: SYSTableData,
  def: SYSTableDef,
  rowKey: string,
  fnName: string,
  validKeys: string[],
  selectedKeys: string[],
  noPromptDependentFromHookResult?: boolean,
  title?: string
): Promise<boolean> => {
  let connectedFormState: SYSFormState
  let connectedForm: { formType: string; formId: string }
  let confirmContentFn = HookTableRowFnConfirm

  if (customFnDef?.prompt?.connectedForm) {
    let formIdKey
    if (rowKey) {
      formIdKey = rowKey
    } else {
      formIdKey = "Multi"
    }
    connectedFormState = await FormInit(
      <SYSForm>`SYSTableRowFn${fnKey}`,
      customFnDef.prompt.connectedForm.formFields,
      {
        formId: `${def.id}_OvlSep_${formIdKey}`,
        feature: <SYSFeature>`${def.feature}`,
        subFeature: fnKey,

        tableDefId: def.id,
      }
    )
    connectedForm = {
      formType: connectedFormState.formType,
      formId: connectedFormState.formId,
    }
    connectedFormBehaviour.formType = connectedFormState.formType
    connectedFormBehaviour.formId = connectedFormState.formId
    connectedFormBehaviour.fieldsResetType =
      customFnDef.prompt.connectedForm.fieldsResetType
  }

  if (!title) {
    title = _GetTableDialogTitle(
      def,
      fnName,
      1,
      data.data,
      validKeys[0],
      !!customFnDef?.whenCalledFromSelection?.grouped
    )
  }

  let cancelPrompt = false
  if (customFns?.hooks?.[confirmContentFn]) {
    let invalidKeys: string[] = []
    let toSet: HookTableRowFnConfirmToSet_Type = {
      systemConfirmContent,
      systemValidationContent,
      customConfirmContent,
      invalidKeys,
    }
    if (noPromptDependentFromHookResult) {
      // we have a usecase where we want to do some further checking in the confirm hook and cancel
      // use case: OvlCopy,OvlEdit do some further checking if action makes sense
      toSet.noPrompt = true
    }
    await customFns.hooks[confirmContentFn](<HookTableRowFnConfirm_Type>{
      connectedFormState,
      defaultFormBodyRenderer: () =>
        FormBodyRenderer(
          connectedFormState,
          {
            feature: connectedFormState.feature,
            columns: connectedFormState.fields,
            options: { edit: { groups: {} } },
          },

          data.data[rowKey],
          data,
          rowKey
        ),
      isMulti: rowKey === undefined,
      fnKey: fnKey,
      rows: data.data,
      fnName,
      title,
      tableDefId: def.id,
      selectedKeys,
      validKeys,
      toSet,
    })
    systemConfirmContent = toSet.systemConfirmContent
    systemValidationContent = toSet.systemValidationContent
    customConfirmContent = toSet.customConfirmContent
    if (toSet.cancelFn) {
      return false
    }
    if (toSet.noPrompt) {
      cancelPrompt = true
    }
    // using splice here because the ref still should work
    toSet.invalidKeys.forEach((k) => {
      validKeys.splice(validKeys.indexOf(k), 1)
    })
  } else if (noPromptDependentFromHookResult) {
    cancelPrompt = true
  } else {
    // if there is no hook but a connected form just use default form renderer
    if (connectedForm) {
      customConfirmContent = FormBodyRenderer(
        connectedFormState,
        {
          feature: connectedFormState.feature,
          columns: connectedFormState.fields,
          options: { edit: { groups: {} } },
        },

        data.data[rowKey],
        data,
        rowKey
      )
    }
  }

  if (!cancelPrompt) {
    let res = await _showTableRowFnConfirm(
      systemValidationContent,
      systemConfirmContent,
      customConfirmContent,
      title,
      connectedForm
    )
    if (res === 2) {
      return false
    }
  }
  return true
}

// export const RowFnProcessErrorHelper = async (
//   process: ProcessReturnType,
//   errors: { [key: string]: string },
//   singleId: string,
//   rowKey: string
// ) => {
//   // make sure that rowFnErrors are either on the _multi instance or on a single executed instance of the process
//   // this way we could execute a batch, then have a error in this batch, fix it and execute that specific line directly on the row
//   // and still the batches errors will persist even that single line now resolved successfully
//   let state = ovl.getState()
//   if (process) {
//     let instancesState =
//       state.ovl.longRunningProcessTracker[process.process].instance
//     let processDef = process.instance.split("_OvlSep_")
//     let rowFnNameId = processDef[1]
//     let tableDefId = processDef[0]
//     let instanceStartWith = processDef.slice(0, 2).join("_OvlSep_")
//     if (singleId) {
//       // if it was a single row execution now only the error from the single row is relevant
//       let processErrors2 = ProcessGetErrors(
//         process.process,
//         `${instanceStartWith}_OvlSep_Multi`
//       )
//       if (processErrors2) {
//         delete processErrors2[singleId]
//       }
//     } else {
//       Object.keys(instancesState)
//         .filter(
//           (k) =>
//             k.indexOf("_OvlSep_Multi") < 0 &&
//             k.startsWith(instanceStartWith) &&
//             instancesState[process.instance].lockedTableKeys[
//               Object.keys(instancesState[k].lockedTableKeys)[0]
//             ]
//         )
//         .forEach((k) => (instancesState[k].errors = {}))
//     }
//     Object.keys(errors).forEach((k) => {
//       if (errors[k]) {
//         process.errors[k] = errors[k]
//       }
//     })

//     let tb = TableGet(<SYSTableDefIds>tableDefId)
//     let fncName = HookTableRowFnCan
//     let fnc = ovl.getActions().app.features[tb.def.feature]?.hooks?.[fncName]
//     if (fnc) {
//       // delete all the errors for which the validate fn doesn not return visible
//       // first we need to get all the errors involved with this processtype
//       let errorsArray = Object.keys(instancesState)
//         .filter((k) => k.startsWith(instanceStartWith))
//         .map((k) => instancesState[k].errors)
//       let errorsMap = errorsArray.reduce((val, k) => {
//         Object.keys(k).forEach((k2) => {
//           //@ts-ignore
//           val[k2] = k
//         })
//         return val
//       }, {})
//       await Promise.all(
//         Object.keys(errorsMap).map(async (k) => {
//           let rowKey = tb.dataDef.index[k]
//           let iCan: HookTableRowFnCan_ReturnType = await fnc(<
//             HookTableRowFnCan_Type
//           >{
//             rowKey,
//             tableDef: tb.def,
//             tableData: tb.dataDef,
//             row: tb.dataDef.data[rowKey],
//           })
//           if (iCan?.[rowFnNameId]?.visibility !== "Visible") {
//             //delete the corresponding error on the process
//             delete errorsMap[k][k]
//           }
//         })
//       )
//     }
//   }
// }

export type CachedRendererData = {
  hasRenderer: boolean
  fn: any
}

export const GetRendererFn = (
  feature: SYSFeature,
  cachedRendererFn: Map<string, CachedRendererData>,
  hookDef: string,
  fieldKey: string
) => {
  let cachedRendererKey = <string>feature + fieldKey + hookDef
  let cachedRenderer = cachedRendererFn.get(cachedRendererKey)
  let rendererFn
  if (!cachedRenderer) {
    let functionName = hookDef.replace("{fieldKey}", fieldKey)
    let fn = ovl.getActions().app.features[feature]
    if (fn?.hooks?.[functionName]) {
      rendererFn = fn.hooks[functionName]
      cachedRendererFn.set(cachedRendererKey, {
        fn: rendererFn,
        hasRenderer: true,
      })
    } else {
      cachedRendererFn.set(cachedRendererKey, {
        fn: undefined,
        hasRenderer: false,
      })
    }
  } else if (cachedRenderer.hasRenderer) {
    rendererFn = cachedRenderer.fn
  }
  return rendererFn
}

// export const TableGetSelectedRowKeys = (def: SYSTableDef): string[] => {
//   let selected = def.uiState.selectedRow
//   return Object.keys(selected).filter((f) => selected[f].selected)
// }

export const _TableSetColumnVisibility = async (
  def: SYSTableDef,
  actions: SYSActions,
  state: SYSState,
  fromInit: boolean
) => {
  let columnsVisible = Object.keys(def.columns)
    .sort((a, b) => def.columns[a].order - def.columns[b].order)
    .reduce((val, k) => {
      let visible: _ColumnVisibility = "Table_Edit_View"
      let column = def.columns[k]

      if (
        column.ui &&
        column.ui.language &&
        column.ui.language !== state.ovl.session.Language
      ) {
        visible = column.ui.translationVisibility
      } else if (column.ui) {
        visible = column.ui.visibility
      }
      val[k] = visible
      return val
    }, {})

  let fn =
    actions.app.features[def.feature]?.hooks?.[HookTableSetColumnVisibility]
  if (fn) {
    await fn(<HookTableSetColumnVisibility_Type>{
      def,
      columnsVisibilityToSet: columnsVisible,
      fromInit,
      screen: ScreenCurrent(),
    })
  }

  def.uiState.columnsRenderingState.visibleInTable = []
  Object.keys(columnsVisible).forEach((f) => {
    def.uiState.columnsRenderingState.columns[f].visibility = columnsVisible[f]
    if (_GetColumnInTableVisibility(columnsVisible[f])) {
      def.uiState.columnsRenderingState.visibleInTable.push(f)
    }
  })
}

export const DoRefreshLookups = (
  actions: SYSActions,
  def: SYSTableDef,
  data: any,
  formState: SYSFormState
) => {
  // if data contains lookup columns and GetLookups defined -> sync Lookups
  // this helps by sending the lookup cols in one query on the backend
  // (and still the lookups will be extended when eg. clicking on the db lookup in list)

  if (data && Array.isArray(data)) {
    if (data.length > 0) {
      let allColumnKeys = Object.keys(data[0])
      let keyToOvlL_Columns = {}
      for (let i = 0; i < allColumnKeys.length; i++) {
        let colKey = allColumnKeys[i]
        if (colKey.indexOf("OvlL_") > 0) {
          let keyAndRest = colKey.split("OvlL_")
          let keyColumn = keyAndRest[0]
          let rest = keyAndRest[1].split("_")
          let lookupKey = rest[0]
          if (keyToOvlL_Columns[keyColumn] === undefined) {
            keyToOvlL_Columns[keyColumn] = { lookupKey, columns: {} }
            keyToOvlL_Columns[keyColumn].alreadySet = new Set()
          }
          let lookupColumnKey = keyAndRest[1].replace(`${lookupKey}_`, "")
          keyToOvlL_Columns[keyColumn].columns[lookupColumnKey] = {
            colKey,
            useFromRow: true,
          }
        }
      }
      let isV2Mode = Object.keys(keyToOvlL_Columns).length > 0
      let functionName = HookLookupsRefresh
      let fn = actions.app.features[def.feature]
      if (fn?.hooks?.[functionName]) {
        let lookups = fn.hooks[functionName](<HookLookupsRefresh_Type>{
          formState,
          tableDefId: def.id,
        })
        if (!lookups) {
          return
        }
        let lookupsKeys = Object.keys(lookups)

        if (isV2Mode) {
          /* now lets see if:
        - there are columns in the lookupDef which are not satisfied 
        - can be used directly from the row
        - are optional and can be ignored
        - have default values
        - is the Id Value
        */

          Object.keys(keyToOvlL_Columns).forEach((f) => {
            let lookupKey = keyToOvlL_Columns[f].lookupKey
            let lookupMainDef = <ListDefinition>lookups[lookupKey]
            if (lookupMainDef === undefined) {
              throw new Error(
                `Ovl Lookup Sync V2: TableDef ${def.id} Lookup ${lookupKey} not found in HookLookupsRefresh results...`
              )
            }
            // keyToOvlL_Columns[f].ignoreNulls =
            //   lookupMainDef.nullHandling?.ignoreIfKeyIsNull === true
            // keyToOvlL_Columns[f].replaceLookupColumnsWithEmptyIfNull =
            //   lookupMainDef.nullHandling
            //     ?.replaceLookupColumnsWithEmptyIfNull === true

            let lookupDef = lookupMainDef.lookupDef
            let lookupDefKeys = Object.keys(lookupDef)
            lookupDefKeys.forEach((f2) => {
              if (!keyToOvlL_Columns[f].columns[f2]) {
                // its not a OvlL mapped one from the result so do some checks
                if (lookupDef[f2].isKey) {
                  keyToOvlL_Columns[f].columns[f2] = { colKey: f, isKey: true }
                } else if (
                  lookupDef[f2].mapToColumn &&
                  allColumnKeys.indexOf(lookupDef[f2].mapToColumn) > -1
                ) {
                  keyToOvlL_Columns[f].columns[f2] = {
                    colKey: lookupDef[f2].mapToColumn,
                    useFromRow: true,
                  }
                } else if (lookupDef[f2].defaultValue) {
                  keyToOvlL_Columns[f].columns[f2] = {
                    colKey: undefined,
                    useDefaultValue: true,
                    defaultValue: lookupDef[f2].defaultValue,
                  }
                } else if (lookupDef[f2].optional) {
                  return
                } else {
                  throw new Error(
                    `Ovl Lookup Sync V2: TableDef ${
                      def.id
                    } Lookup ${lookupKey} not all columns satisfied from result. Missing ${f2}, ColumnDef: ${JSON.stringify(
                      lookupDef[f2]
                    )}`
                  )
                }
              }
            })
          })
          // now that we know everything is ok with the lookups we can refresh them with data from the result
          // OvlL COlumsn can be deleted after refreshing the lookups
          let colsToDelete = Object.keys(data[0]).filter(
            (f) => f.indexOf("OvlL_") > 0
          )

          data.forEach((resultRow) => {
            Object.keys(keyToOvlL_Columns).forEach((f) => {
              let idValue = resultRow[f]
              if (idValue === null || idValue === undefined) {
                return
              }
              if (!keyToOvlL_Columns[f].alreadySet.has(idValue)) {
                keyToOvlL_Columns[f].alreadySet.add(idValue)
                let lookupKey = keyToOvlL_Columns[f].lookupKey
                let lookupData = lookups[lookupKey].data
                let refreshed = false
                Object.keys(keyToOvlL_Columns[f].columns).forEach((c) => {
                  let keyToOvl = keyToOvlL_Columns[f].columns[c]
                  if (resultRow[keyToOvl.colKey] === undefined) {
                    return
                  }
                  refreshed = true
                  if (!lookupData[idValue]) {
                    lookupData[idValue] = {}
                  }

                  if (keyToOvl.isKey) {
                    lookupData[idValue][c] = idValue
                  } else if (keyToOvl.useFromRow) {
                    lookupData[idValue][c] = resultRow[keyToOvl.colKey]
                  } else {
                    lookupData[idValue][c] = keyToOvl.defaultValue
                  }
                  // if (
                  //   lookupData[idValue][c] === null &&
                  //   keyToOvlL_Columns[f].replaceLookupColumnsWithEmptyIfNull ===
                  //     true
                  // ) {
                  //   lookupData[idValue][c] = ""
                  // }
                })
                // call a hook which could add calculated lookup columns
                if (refreshed && fn?.hooks?.[HookLookupRowRefreshed]) {
                  fn?.hooks?.[HookLookupRowRefreshed](<
                    HookLookupRowRefreshed_Type
                  >{
                    lookupDataRow: lookupData[idValue],
                    lookupDefKey: lookupKey,
                  })
                }
              }
            })
            colsToDelete.forEach((c) => delete resultRow[c])
          })
        } else {
          lookupsKeys.forEach((lookupKey) => {
            let lookupDef: ListDefinition = lookups[lookupKey]
            if (lookupDef?.lookupDef === undefined) {
              return
            }
            let mappedDataToLookupColumnKeys = {}
            Object.keys(lookupDef.lookupDef).forEach((lookupColKey) => {
              let matchingDataColumnKey = Object.keys(data[0]).filter(
                (dataColKey) => dataColKey === lookupColKey
              )
              if (matchingDataColumnKey.length > 0) {
                mappedDataToLookupColumnKeys[matchingDataColumnKey[0]] =
                  lookupColKey
              }
            })
            let keyColumnSearch = Object.keys(
              mappedDataToLookupColumnKeys
            ).filter(
              (k) => lookupDef.lookupDef[mappedDataToLookupColumnKeys[k]].isKey
            )
            let keyColumn
            let dataKeyColumn
            if (keyColumnSearch.length > 0) {
              keyColumn = mappedDataToLookupColumnKeys[keyColumnSearch[0]]
              dataKeyColumn = keyColumnSearch[0]
            }
            if (!keyColumn) {
              // get it from name convention Lookup Key = LookupColumn Key
              keyColumnSearch = Object.keys(
                mappedDataToLookupColumnKeys
              ).filter((k) => mappedDataToLookupColumnKeys[k] === lookupKey)
              if (keyColumnSearch.length < 1) {
                // throw new Error(
                //   "SYS no lookup key found. (Not in LookupCol.isKey or by LookupKey == LookupColKey"
                // )
                return
              }
              keyColumn = mappedDataToLookupColumnKeys[keyColumnSearch[0]]
              dataKeyColumn = keyColumnSearch[0]
            }

            if (!lookups[lookupKey].data) {
              lookups[lookupKey]["data"] = {}
            }
            let lookupsData = lookups[lookupKey]["data"]
            data.forEach((row) => {
              let keyVal = row[dataKeyColumn]
              if (!lookupsData[keyVal]) {
                // its a new one
                lookupsData[keyVal] = {}
              }
              Object.keys(mappedDataToLookupColumnKeys).forEach(
                (mappedDataColumnKey) => {
                  let lookupColumnKey =
                    mappedDataToLookupColumnKeys[mappedDataColumnKey]
                  lookupsData[keyVal][lookupColumnKey] =
                    row[mappedDataColumnKey]
                }
              )
            })
          })
        }
      }
      //delete lookup list cache
      _cachedFieldLists.forEach((_value, key) => {
        if (key.startsWith(`@@table:${def.id}`)) {
          _cachedFieldLists.set(key, undefined)
        }
      })
    }
  } else {
    throw new Error("SYS Error: result needs to be an array")
  }
}

export const _ResetFilters = async (
  def: SYSTableDef,
  clear: boolean = false
) => {
  // let def = tableDef.def
  let columns = def.columns
  let uiState = def.uiState
  if (!clear) {
    // if clear = false we are already in forcedFilter Context
    uiState.filter.filter.forcedFilterKeys = undefined
  }
  await Promise.all(
    Object.keys(columns).map(async (f) => {
      let col = def.columns[f]
      // todo separate loop for range filters
      // now just do it here
      if (col.filter.rangeFilter) {
        if (clear) {
          uiState.filter.columns[f].rangeFilter.selected = "none"
        } else {
          uiState.filter.columns[f].rangeFilter = JSON.parse(
            JSON.stringify(col.filter.rangeFilter)
          )
        }
      }
      if (col.filter.columnFilter) {
        let list = []
        if (!clear) {
          list = col.filter.columnFilter.list
        }
        if (
          !(await TableColumnFilter(
            def.id,
            f,
            JSON.parse(JSON.stringify(list)),
            { _innerEvent: true }
          ))
        ) {
          return
        }
      }
    })
  )

  await Promise.all(
    Object.keys(uiState.filter.filterCustom).map(async (m) => {
      if (def.options.initial_filterCustom[m]) {
        let val = def.options.initial_filterCustom[m].active
        if (clear) {
          val = false
        }
        if (!(await TableCustomFilter(def.id, m, val, { _innerEvent: true }))) {
          return
        }
      }
    })
  )

  let filterAttributes = uiState.filter.filterAttributes
  let filterAttributesInitial = def.options.initial_rowAttributes
  await Promise.all(
    Object.keys(filterAttributesInitial).map(async (defKey) =>
      Object.keys(filterAttributesInitial[defKey].attributes)
        .concat(
          Object.keys(
            filterAttributes[defKey]?.attributes
              ? filterAttributes[defKey].attributes
              : {}
          )
        )

        .map(async (attributeKey) => {
          let val = !!filterAttributesInitial[defKey].attributes[attributeKey]
          if (clear) {
            val = false
          }

          if (
            !(await TableAttributeFilter(def.id, defKey, attributeKey, val, {
              _innerEvent: true,
            }))
          ) {
            return
          }
        })
    )
  )

  // last but not least
  // if the headermenu is open and rangefilter active then those textbox fields needs to be updated as well

  let instance = DialogIsOpen("SYSTableHeaderMenu", "2") ? "2" : "1"
  let formState = FormGet("SYSHeaderMenuFilter", { instance }).formState
  if (formState && formState.tableDefId) {
    let fromColumnHeaderColumn = TableGet(formState.tableDefId).def.uiState
      .headerSelected
    if (fromColumnHeaderColumn) {
      let rangeFilterState =
        def.uiState.filter?.columns?.[fromColumnHeaderColumn]?.rangeFilter
      if (rangeFilterState) {
        await FieldSetVal(
          formState.fields["HeaderMenuRangeFilterType"],
          rangeFilterState.selected,
          {
            ignoreValueChanged: true,
          }
        )
        await FieldSetVal(
          formState.fields["HeaderMenuRangeFilterValue"],
          rangeFilterState.val,
          {
            ignoreValueChanged: true,
          }
        )
        await FieldSetVal(
          formState.fields["HeaderMenuRangeFilterValue2"],
          rangeFilterState.val2,
          {
            ignoreValueChanged: true,
          }
        )
        FormSetUnDirty(formState)
      }
    }
  }

  // uiState.filter.filterCustom = JSON.parse(
  //   JSON.stringify(def.options.initial_filterCustom)
  // )
  uiState.filter.filter.value = ""
  uiState.filter.filter.showSelected = false
}
export const _ResetSort = (def: SYSTableDef) => {
  let sortDef = def.options.initial_sort
  let initialSort
  if (isMobile() && sortDef.fieldMobile) {
    initialSort = {
      useValue: sortDef.useValue,
      direction: sortDef.directionMobile,
      field: sortDef.fieldMobile,
    }
  } else {
    initialSort = {
      direction: sortDef.direction,
      field: sortDef.field,
      useValue: sortDef.useValue,
    }
  }
  def.uiState.sort.sort = initialSort
  def.uiState.sort.sortCustom = JSON.parse(
    JSON.stringify(def.options.initial_sortCustom)
  )
}

export const GetTableKeys = <T>(
  def: SYSTableDef,
  dataDef: SYSTableData,
  options?: _GetTableOptions<T>
) => {
  let onlyUseTheseKeys = options?.onlyUseTheseKeys
  let resKeys = []
  if (def.initialised) {
    let opt = options
    let data = dataDef.data
    let keys: string[]
    if (onlyUseTheseKeys) {
      keys = onlyUseTheseKeys
    } else {
      keys = Object.keys(data).filter(
        (f) => (data[f].OvlSysState as OvlSysColumn).rowState.state !== "Add"
      )
    }

    if (opt.byKey !== undefined) {
      return [opt.byKey]
    } else if (opt.byIdValue !== undefined) {
      if (dataDef.index[opt.byIdValue] !== undefined) {
        resKeys = [dataDef.index[opt.byIdValue]]
      }
    } else if (opt.byCustomFilter !== undefined) {
      resKeys = keys.filter((k) => opt.byCustomFilter(data[k], k))
    } else if (opt.byCustomFilterInVisible !== undefined) {
      resKeys = def.uiState.dataFilteredAndSorted
        .filter(
          (f) => (data[f].OvlSysState as OvlSysColumn).rowState.state !== "Add"
        )
        .filter(
          (f) =>
            onlyUseTheseKeys === undefined || onlyUseTheseKeys.indexOf(f) > -1
        )
        .filter((k) => opt.byCustomFilterInVisible(data[k], k))
    } else {
      if (!opt.byKeys) {
        opt.byKeys = "Visible"
      }
      if (opt.byFilter) {
        let filter = opt.byFilter
        let filterKeys = Object.keys(filter)
        resKeys = keys.filter(
          (k) =>
            filterKeys.filter((f) => filter[f] === data[k][f]).length ===
            filterKeys.length
        )
      } else if (opt.byKeys === "All") {
        resKeys = keys
      } else if (opt.byKeys === "StaticFiltered") {
        resKeys = keys
        let staticFilter = def.uiState.filter.filter.static
        let filterKeys = Object.keys(staticFilter)
        resKeys = keys.filter(
          (k) =>
            filterKeys.filter((f) => staticFilter[f] === data[k][f]).length ===
            filterKeys.length
        )
      } else if (opt.byKeys === "RowControlVisible") {
        resKeys = Object.keys(def.uiState.selectedRow).filter(
          (f) =>
            def.uiState.selectedRow[f].showNav &&
            def.uiState.selectedRow[f].selected
        )
      } else if (opt.byKeys.indexOf("Selected") > -1) {
        let staticFilter = def.uiState.filter.filter.static
        let filterKeys = Object.keys(staticFilter)

        let selected = def.uiState.selectedRow
        resKeys = Object.keys(selected)
          .filter(
            (f) =>
              onlyUseTheseKeys === undefined || onlyUseTheseKeys.indexOf(f) > -1
          )
          .filter(
            (f) =>
              selected[f].selected &&
              ((data[f].OvlSysState as OvlSysColumn).rowState
                .lastChangeBelongsToTableDefId === def.id ||
                filterKeys.filter((f2) => staticFilter[f2] === data[f][f2])
                  .length === filterKeys.length)
          )
        if (opt.byKeys === "SelectedTimeDesc") {
          resKeys = resKeys.sort(
            (a, b) => selected[b].count - selected[a].count
          )
        } else if (opt.byKeys === "SelectedTimeAsc") {
          resKeys = resKeys.sort(
            (a, b) => selected[a].count - selected[b].count
          )
        }
      } else if (opt.byKeys === "Visible") {
        resKeys = def.uiState.dataFilteredAndSorted.filter(
          (f) =>
            (data[f].OvlSysState as OvlSysColumn).rowState.state !== "Add" &&
            (onlyUseTheseKeys === undefined || onlyUseTheseKeys.indexOf(f) > -1)
        )
      }
    }
  }
  return resKeys
}

export const _GetFilterSortDifferentiator = (defId: SYSTableDefIds) => {
  let def = TableGet(defId).def
  let filterSortStoreDifferentiator
  if (def.options?.filterSortSettings?.staticFilterKey) {
    filterSortStoreDifferentiator =
      def.uiState.filter.filter.static[
        def.options.filterSortSettings.staticFilterKey
      ]
  }
  let fn =
    ovl.getActions().app.features?.[def.feature]?.hooks?.[
      HookTableFilterSortSettingsDifferentiator
    ]

  if (fn) {
    filterSortStoreDifferentiator = (<
      HookTableFilterSortSettingsDifferentiator_ReturnType
    >fn(<HookTableFilterSortSettingsDifferentiator_Type>{
      differentiator: filterSortStoreDifferentiator,
      def,
    })).differentiator
  }
  // if (filterSortStoreDifferentiator === undefined) {
  //   return ""
  // }
  return filterSortStoreDifferentiator
}

export const _GetFilterSortMainDifferentiator = (defId: SYSTableDefIds) => {
  let def = TableGet(defId).def
  let filterSortStoreDifferentiator = ""
  if (def.options?.filterSortSettings?.staticFilterKey) {
    filterSortStoreDifferentiator =
      def.uiState.filter.filter.static[
        def.options.filterSortSettings.staticFilterKey
      ]
  }
  return filterSortStoreDifferentiator
}

export const FormBodyRenderer = (
  formState: SYSFormState,
  def: SYSTableDef,
  row?: any,
  data?: any,
  rowKey?: string
) => {
  let formHookFns = ovl.getActions().app.features[formState.feature]?.hooks
  let fields = formState.fields
  let formValidationHint
  if (FormHasVisibleSummaryErrors(formState)) {
    formValidationHint = html`
      <ovl-formvalidationhint
        .props=${() => formState}
      ></ovl-formvalidationhint>
    `
  }

  let columns = def.columns

  let hookFormBodyRender =
    formHookFns?.[
      HookFormGetBodyRender.replace("{subFeature}", formState.subFeature)
    ]

  if (hookFormBodyRender) {
    let getBodyHookRes: HookFormGetBodyRender_ReturnType = hookFormBodyRender({
      fields: fields,
      formState: formState,
      tableDef: def,
    } as HookFormGetBodyRender_Type)
    return html`${getBodyHookRes.res}${formValidationHint}`
  }

  // group by editGroups
  let groupedColumns = {}
  Object.keys(columns)
    .sort((a, b) => columns[a].groups.group - columns[b].groups.group)
    .forEach((k) => {
      if (!groupedColumns[columns[k].groups.group]) {
        groupedColumns[columns[k].groups.group] = {}
      }
      groupedColumns[columns[k].groups.group][k] = columns[k]
    })
  return html`
    ${Object.keys(groupedColumns).map((g) => {
      let groupsState = def.options.edit.groups[g]
      let formStateGroupsState = formState.groups.edit[<number>(<any>g)]
      if (
        formStateGroupsState === undefined ||
        formStateGroupsState.removedAndIrrelevantInCurrentSituation
      ) {
        return html`<!---->`
      }
      let editGroupClassName = `editgroup_${g}`
      let editGroupLabelClassName = `grp__label`
      let editGroupFieldsClassName = `grp__fields`
      let groupLabel
      if (groupsState) {
        if (formStateGroupsState.visible !== "false") {
          if (groupsState.className) {
            editGroupClassName = `ovl-editgroupuser ${groupsState.className}`
          } else {
            editGroupClassName = "ovl-editgroupuser"
          }
          groupLabel = groupsState.translationKey
          if (groupLabel) {
            groupLabel = html`<label
              class="fd-form-label ${editGroupLabelClassName}"
              >${T(groupLabel)}</label
            >`
          }
        } else {
          return html`<!---->`
        }
      }
      let columnRenderingState = def?.uiState?.columnsRenderingState?.columns
      return html` <div class="ovl-editgroup ${editGroupClassName}">
        ${groupLabel}
        <div class="${editGroupFieldsClassName}">
          ${Object.keys(groupedColumns[g])
            .sort((a, b) => columns[a].order - columns[b].order)
            .filter((f) => {
              let valid = true
              let visibility = columnRenderingState?.[f]?.visibility
              if (visibility && visibility.indexOf("Edit") < 0) {
                valid = false
              }
              return valid
            })
            .map((k) => {
              let rendererFn = GetRendererFn(
                def.feature,
                cachedRendererFn,
                HookFormCustomControlRender.replace("{subFeature}", ""),
                k
              )
              let uiItem
              let id = "ovlRFNFocus_" + k
              if (rendererFn) {
                uiItem = rendererFn(<HookFormCustomControlRender_Type>{
                  field: fields[k],
                  formState: formState,
                  id,
                })
              } else {
                uiItem = FormGetControl(fields[k], def)
              }

              return html` <div data-col=${k}>${uiItem}</div> `
            })}
        </div>
      </div>`
    })}
    ${formValidationHint}
  </div>`
}

export const _handleRowFnConfirmFieldsReset = (
  connectedFormBehaviour: {
    formType: string
    formId: string
    fieldsResetType: RowFnConfirmContentFieldsReset_Type
  },
  feedback: any
) => {
  if (connectedFormBehaviour) {
    let form = FormGet(<SYSForm>connectedFormBehaviour.formType, {
      instance: connectedFormBehaviour.formId,
    })

    let errorCount = Object.keys(feedback).filter(
      (k) => feedback[k].type === "error"
    ).length
    let warningCount = Object.keys(feedback).filter(
      (k) => feedback[k].type === "warning"
    ).length
    let resetFields = false
    if (connectedFormBehaviour.fieldsResetType === "Always") {
      resetFields = true
    } else if (errorCount === 0) {
      if (
        connectedFormBehaviour.fieldsResetType === "ResultHasOnlySuccess" &&
        warningCount === 0
      ) {
        resetFields = true
      } else if (
        connectedFormBehaviour.fieldsResetType ===
        "ResultHasSuccessAndOrWarning"
      ) {
        resetFields = true
      }
    }

    if (resetFields) {
      FormReset(form)
    }
  }
}

export const _handleRowFnEndpointData = async (
  fnKey: string,
  clientId: string,
  url: string,
  fnResultToSet: RowFunctionResultToSet,
  data: SYSTableData,
  def: SYSTableDef,
  selectedObjects: string[],
  connectedConfirmFormState: SYSFormState,
  process: _ProcessReturnType,
  trackingPromise: Promise<boolean>
) => {
  let ids = selectedObjects.map((k) => data.data[k][def.database.dataIdField])
  let formValues = undefined
  let files = []
  if (connectedConfirmFormState) {
    let fields: SYSFields = connectedConfirmFormState.fields
    formValues = Object.keys(fields)
      .filter((f) => !fields[f].schema?.isCalculatedColumn)
      .reduce((val, k) => {
        val[k] = fields[k].convertedValue
        return val
      }, {})

    files = Object.keys(fields)
      .filter(
        (k) => fields[k]._state._files && fields[k]._state._files.length > 0
      )
      .map((k) => {
        return { fieldKey: k, files: fields[k]._state._files }
      })
  }

  let screenCurrentState = ScreenCurrentState()
  let uiContext: UIContextType = {
    feature: <string>def.feature,
    formType: undefined,
    formId: undefined,
    screen: screenCurrentState?.screen,
    screenInstance: screenCurrentState?.instance,
  }

  let withFiles = ""
  let postData: any = {
    ids,
    formValues,
    staticFilter: TableStaticFilterGet(def.id),
    tracking: { fnKey: `${process.tableFn.tableDefId}_${fnKey}`, clientId },
    uiContext,
  }

  if (files.length > 0) {
    // we have files
    withFiles = "withfiles"
    let formData = new FormData()
    formData.append("tableData", JSON.stringify(postData))
    files.forEach((f, i) => {
      for (let z = 0; z < f.files.length; z++) {
        formData.append(`${f.fieldKey}`, f.files[z])
      }
    })
    postData = formData
  }
  try {
    let sendres = await Data(`${url}${withFiles}`, postData, {
      isFormData: files.length > 0,
    })
    if (sendres.status === 202) {
      if (!trackingPromise) {
        throw new Error(
          `Ovl: tracking not enabled but backend wants tracking...tabledef: ${def.id}`
        )
      }
      await trackingPromise
    } else {
      await Promise.all(
        sendres.data.map(async (r) => {
          await fnResultToSet.setFromResult(r)
        })
      )
    }
  } catch (error) {
    let type = error?.type
    let msg = error?.message ? error.message : ""
    let isWarning = false

    if (error?.status == 400 && error.type === "SYSTooManyRequestsFromRowFn") {
      msg = T("SYSTooManyRequestsFromRowFn")
      isWarning = true
    }
    if (Object.keys(process.tableFn.lockedTableKeys).length > 0) {
      Object.keys(process.tableFn.lockedTableKeys).forEach((k) => {
        if (isWarning) {
          fnResultToSet.setWarning(k, msg, type)
        } else {
          fnResultToSet.setError(k, msg, type)
        }
      })
    } else {
      if (isWarning) {
        fnResultToSet.setWarning("", msg, type)
      } else {
        fnResultToSet.setError("", msg, type)
      }
    }
    return
  }
}
export const _handleRowFnTracking = (
  trackType: RowFnTrackingInterval,
  trackStatusIntervall: any,
  fnKey: string,
  clientId: string,
  process: _ProcessReturnType,
  fnResultToSet: RowFunctionResultToSet,
  fnName: string
): Promise<boolean> => {
  let trackingPromise = new Promise<boolean>((resolve, reject) => {
    let intervallMS
    switch (trackType) {
      case "Every2Secs":
        intervallMS = 2000
        break
      case "Every5Secs":
        intervallMS = 5000
        break
      case "Every10Secs":
        intervallMS = 10000
        break
      case "Every20Secs":
        intervallMS = 20000
    }
    let interValRunning = false
    let startedTimestamp = new Date().getTime()
    trackStatusIntervall.id = setInterval(async () => {
      if (interValRunning) {
        return
      }
      interValRunning = true
      try {
        let status = await Data("users/tracktablerowfn", {
          fnKey: `${process.tableFn.tableDefId}_${fnKey}`,
          clientId,
        })
        if (status.data.currentResults) {
          await Promise.all(
            status.data.currentResults.map((r) => {
              return fnResultToSet.setFromResult(r)
            })
          )
        }

        if (
          !status.data
            .isTracking /*|| !ProcessIsRunning(process.process, process.instance) */
        ) {
          if (status.data.currentIndex > -1) {
            clearInterval(trackStatusIntervall.id)
            resolve(true)
          } else {
            // if still getting -1 after 2 minutes we have a timeout
            // that means server for some reason has no mecached result ready which the server fn should prepare early in the process
            let ts = new Date().getTime()
            if (ts - startedTimestamp > 1000 * 60 * 2) {
              throw new Error("Timed out...")
            }
          }
          return
        }

        let hint
        if (status.data.currentTask) {
          hint = status.data.currentTask
        } else {
          hint = `(${status.data.currentIndex + 1} / ${status.data.totalCount})`
        }

        if (process?.trackedSnackId) {
          let cancellingHint = process.cancelled
            ? " - " +
              (T("SYSRowFnCancelling") !== "SYSRowFnCancelling"
                ? T("SYSRowFnCancelling")
                : "Cancelling...")
            : ""
          SnackTrackedReplaceContent({
            key: process.trackedSnackId,
            content: T(process.process, [fnName, hint]) + cancellingHint,
          })
        }

        // if (!status.data.isTracking && status.data.currentIndex < 0) {
        //   return
        // }
      } catch (error) {
        let type = error.type
        if (!type) {
          type = error
        }
        Object.keys(process.tableFn.lockedTableKeys).forEach((k) => {
          fnResultToSet.setError(k, type, type)
        })

        clearInterval(trackStatusIntervall.id)
        resolve(false)
        return
      } finally {
        interValRunning = false
      }
    }, intervallMS)
  })
  return trackingPromise
}

export const _AddRowToDisplayedArray = (def: SYSTableDef, key: string) => {
  if (def.uiState.dataFilteredAndSorted.indexOf(key) < 0) {
    if (def.options.addedRowsPosition === "bottom") {
      //push it, if not done already in inline edit
      def.uiState.dataFilteredAndSorted.push(key)
    } else {
      def.uiState.dataFilteredAndSorted.unshift(key)
    }
    def.uiState.rowsCount++
  }
}

export const _DoRebuildAndFixPage = (def: SYSTableDef) => {
  /*
  We have different Actions triggered either by User clicking the corresponding action or by using APIs which loads TableRow(s) from Server:
  -- (R) Refresh Button -> gets multiple rows from Server
  -- (E) Editing an existing Row  -> gets the updated row from Server (Eventually containing updates from other Users meanwhile)
  -- (A) Adding a new Row -> gets the row from Server (after it was saved)
  -- (D) Deleting a row -> needs to set deletedKeys (see further down)
  -- (F) Executing a rowFn which sets fieldresult or datatorefresh -> gets the added/updated row (resp. fields in case fieldresult) from server
  Whenever there is Data retrieved from Server we need to check if the TableDef on which we operate still displays valid information.
  
  Please keep in mind that for Feedback Sorting this part is handled externally

  Thats why we keep track on the the following state for those actions:

    export type _RebuildType = {
    deletedKeys?: { [key: string]: boolean }
    addedKeys?: { [key: string]: boolean }
    updatedKeys?: { [key: string]: boolean }
    needsRefresh?: boolean
    changed: { [key: string]: ServerData_ChangedType }
    }

  The visible Rows itself are depending on:
  - Filters (Tabletext Filter, ColumnFilter, ColumnRangeFilter, CustomFilter)
  - Sort (Field Sort, Custom Sort)
  - Aggregates 
  - Paging information (Page x of y)

  That means we should update the view as well when the returned data affects it.
  -> The updated cell values itself are automatically displayed thanks to ovl-reactive layer
  -> but it could be that now filter/sort/aggregates are out of synch

  That means we need some logic to set the needsRefresh.
  Setting needsRefresh to true can have 2 consequences:

  if def.features.autoRebuild is: "AfterModifyOrDelete" or "AfterModifyOrDeleteOrAdd" and one of the actions is met. 
  -> Then we do an automatic Rebuild(...) on the current def
  -> otherwise thanks to this flag set the user gets an indication (Rebuild Button) on the UI

  Paging is different and a less expensive action. 
  Thats why we always set it when needed on the current def already and set needsRefresh on the other defs
  Rebuild(...) always will also adjust the paging


*/
  // we need access to all tableDefs, not just the current one
  let tb = TableGet(def.id)

  let currentDefId = def.id

  // we do 2 passes
  // first one just sets needsRefresh
  // second one takes care of Paging Settings

  // and in the end we check if current def can be auto rebuild
  let rowChanges = def.uiState.rebuild.changed
  Object.keys(tb.dataDef.tableDef).forEach((tdKey) => {
    let td: SYSTableDef = tb.dataDef.tableDef[tdKey]

    if (!td.initialised) {
      // if not intialised (= for sure not displayed somewhere) we just proceed with next one
      // or if already a refresh needed we do not need to evaluate again...
      return
    }

    let rebuildTracking = td.uiState.rebuild

    let deletedKeys = Object.keys(rebuildTracking.deletedKeys)
    let deleted = deletedKeys.length > 0

    let hasAggregates =
      td.uiState.rowsCount > 0 &&
      !td.options.tableGroupAggregates.disabled &&
      (td.options.tableGroupAggregates.aggregates.length > 0 ||
        td.options.tableGroupAggregates.groups.length > 0)

    let deletedInside = false
    if (hasAggregates) {
      if (deleted) {
        // if the deleted key was inside
        //  the displayed area
        deletedInside = deletedKeys.some((dk) =>
          td.uiState.dataFilteredAndSorted.some((tk) => tk === dk)
        )
      }
    }
    if (deleted) {
      deletedKeys.forEach((key) => {
        let i = td.uiState.dataFilteredAndSorted.indexOf(key)
        if (i > -1) {
          td.uiState.dataFilteredAndSorted.splice(i, 1)
          td.uiState.rowsCount--
          delete deletedKeys[key]

          delete td.uiState.selectedRow[key]
          if (deletedInside) {
            if (td.uiState.rowsCount > 0) {
              rebuildTracking.needsRefresh = true
            }
            return
          }
        }
      })
    }

    // we set added and updatedkeys according to changedInfo so we can quickly check them
    Object.keys(rowChanges).forEach((k) => {
      let changed: ServerData_ChangedType = def.uiState.rebuild.changed[k]
      if (changed.isAdd) {
        rebuildTracking.addedKeys[k] = true
      } else {
        rebuildTracking.updatedKeys[k] = true
      }
    })
    if (td.uiState.rebuild.needsRefresh) {
      return
    }

    let addedKeys = Object.keys(rebuildTracking.addedKeys)
    let added = addedKeys.length > 0

    let updatedKeys = Object.keys(rebuildTracking.updatedKeys)
    let updated = updatedKeys.length > 0

    // and another early out if the
    // check if result is in filter
    // so we execute the changed data with the current filtersettings from td
    // if we have no results and we are not on the current def -> no rebuild on the def
    let currentData = Object.keys(rowChanges).reduce((val, k) => {
      // it could have been deleted meanwhile
      // Test Add A row, Delete it, add it again was failing because of this missing check
      if (tb.rows[k] !== undefined) {
        val[k] = tb.rows[k]
      }
      return val
    }, {})

    let currentDataFilteredKeys = _TableFilterFn({
      data: { data: currentData, tableDef: undefined },
      def: td,
    })
    if (currentDataFilteredKeys.length === 0) {
      // currentdef gets the added row automatically at top or bottom
      // in case of add on current the also rebuild
      if (currentDefId === td.id && added) {
        rebuildTracking.needsRefresh = true
      }
      //all other defs if not affected -> all good, go to next def
      return
    } else {
      // filtered result as visible in the other def affected
      // -> needsRebuild (needsRefresh)

      if (updated || added) {
        rebuildTracking.needsRefresh = true
      }
      return
    }
  })

  // 2nd pass for the page settings
  Object.keys(tb.dataDef.tableDef).forEach((tdKey) => {
    let td: SYSTableDef = tb.dataDef.tableDef[tdKey]
    if (!td.initialised) {
      return
    }
    let rebuildTracking = td.uiState.rebuild
    let addedKeys = Object.keys(rebuildTracking.addedKeys)
    let added = addedKeys.length > 0

    let deletedKeys = Object.keys(rebuildTracking.deletedKeys)
    let deleted = deletedKeys.length > 0

    if (added || deleted) {
      if (def.features.page) {
        if (currentDefId === td.id) {
          let paging = def.uiState.paging
          let pages = Math.ceil(def.uiState.rowsCount / paging.pageSize) - 1
          if (pages > -1) {
            if (paging.page > pages) {
              paging.page = pages
            } else if (paging.page < 0) {
              paging.page = 0
            }
          }
        } //else {
        //   // we just say it needs refresh if there is another (not the active one) tabledef using paging
        //   rebuildTracking.needsRefresh = true
        // }
      }
    }
  })

  // now if there are any needsRefresh but rowsCount is 0
  Object.keys(tb.dataDef.tableDef).forEach((tdKey) => {
    let td: SYSTableDef = tb.dataDef.tableDef[tdKey]
    if (!td.initialised) {
      return
    }
    let allCount = Object.keys(tb.dataDef.data).length
    if (td.uiState.rebuild.needsRefresh && allCount === 0) {
      td.uiState.rebuild.needsRefresh = false
    }
  })

  // now if there is a changed to b and a is filtered in a table and b in another
  // case b is handled but a doesn't know that "it has lost an a"
  Object.keys(tb.dataDef.tableDef).forEach((tdKey) => {
    let td: SYSTableDef = tb.dataDef.tableDef[tdKey]
    if (!td.initialised || td.uiState.rebuild.needsRefresh === true) {
      return
    }

    let filterCustomDef = td.options.initial_filterCustom

    // let activeSortCols = {}
    // if (td.uiState.sort.sortCustom.selected) {
    //   activeSortCols[td.uiState.sort.sortCustom.selected] = true
    // }
    // if (td.uiState.sort.sort.field) {
    //   activeSortCols[td.uiState.sort.sort.field] = true
    // }

    let rebuildTracking = td.uiState.rebuild
    let changedKeys = Object.keys(rebuildTracking.updatedKeys)
    let changed = changedKeys.length > 0

    if (!changed) {
      return
    }
    if (
      changedKeys.some((ck) =>
        td.uiState.dataFilteredAndSorted.some((dfk) => ck === dfk)
      )
    ) {
      rebuildTracking.needsRefresh = true
      return
    }
    // let activeCustomFilterKeys = Object.keys(filterCustomDef).filter(
    //   (fcd) => !!td.uiState.filter.filterCustom[fcd]?.active
    // )

    // let activeFilterCols = activeCustomFilterKeys.reduce((val, fcd) => {
    //   Object.keys(filterCustomDef[fcd].involvedColumnKeys).forEach((ick) => {
    //     val[ick] = true
    //   })
    //   return val
    // }, {})

    // // staticFilter
    // let staticFilter = td.uiState.filter.filter.static
    // Object.keys(staticFilter).forEach((k) => {
    //   activeFilterCols[k] = true
    // })

    // // columnfilter and rangefilter
    // let columnFilterDef = td.uiState.filter.columns
    // Object.keys(columnFilterDef).forEach((cfd) => {
    //   if (
    //     columnFilterDef[cfd].columnFilter?.type === "list" ||
    //     columnFilterDef[cfd].rangeFilter?.selected
    //   ) {
    //     activeFilterCols[cfd] = true
    //   }
    // })

    // let hasActiveFilters = Object.keys(activeFilterCols).length > 0
    // if (hasActiveFilters) {
    //     // now check if the changed field is involved in a filter
    //     if (changedKeys.some)
    // }
  })

  // check if the current one can already be rebuilded
  if (def.uiState.rebuild.needsRefresh) {
    let td: SYSTableDef = def
    let rebuildTracking = td.uiState.rebuild
    let addedKeys = Object.keys(rebuildTracking.addedKeys)
    let added = addedKeys.length > 0

    let updatedKeys = Object.keys(rebuildTracking.updatedKeys)
    let updated = updatedKeys.length > 0

    let deletedKeys = Object.keys(rebuildTracking.deletedKeys)
    let deleted = deletedKeys.length > 0

    if (
      (td.features.autoRebuild === "AfterModifyOrDelete" &&
        (updated || deleted)) ||
      (td.features.autoRebuild === "AfterModifyOrDeleteOrAdd" &&
        (updated || deleted || added))
    ) {
      TableRebuild(td.id)
      return
    }
  }
}

export type _MergeRowHookParamsType = {
  fn: any
  tableData: SYSTableData
  defId: SYSTableDefIds
}

/*
changed: a helper set for the HookTableServerData
*/
export const _DoServerDataConversionSyncAndBuildUpRebuildAndChangedInfo = (
  rowKey: string,
  clientConversion: _ClientConversion,
  serverRow: { [key: string]: any },
  currentRow: { [key: string]: any },
  rebuild: _RebuildType,
  isAdd: boolean,
  mergeRowHookParams: _MergeRowHookParamsType,
  checkForOtherModifications?: boolean,
  changedColumns?: { [key: string]: any }
): ServerData_ChangedType => {
  let changed: ServerData_ChangedType = { columns: {}, isAdd }
  // keep a list of other modified (if another user modified a field and its not in the data.fieldChangeReportingIgnoreList (like updateTime, updateDate which gets done by server))
  let otherModified = new Set<string>()

  if (mergeRowHookParams) {
    if (
      mergeRowHookParams.fn(<HookTableServerBeforeMergeRow_Type>{
        rowKey,
        serverRow: serverRow,
        localRowJustForChecking: currentRow,
        tableData: mergeRowHookParams.tableData,
        defId: mergeRowHookParams.defId,
      })
    ) {
      // if handled by hook no need for default dance
      return changed
    }
  }

  Object.keys(serverRow).forEach((columnKey) => {
    // do the hard conversions
    // todo: remove this from columndefs in tablecolumndef
    let setToNullIf = clientConversion[columnKey]?.setToNullIf
    let setToValueIfNull = clientConversion[columnKey]?.setToValueIfNull
    if (setToNullIf && setToNullIf === serverRow[columnKey]) {
      serverRow[columnKey] = null
    }
    if (setToValueIfNull && serverRow[columnKey] === null) {
      serverRow[columnKey] = setToValueIfNull
    }
    if (isAdd) {
      // add is a different beast
      // no value comparison
      // -> treat column as changed
      // -> mark as addedkey
      changed.columns[columnKey] = true
      //rebuild.addedKeys[rowKey] = true
      // still do the conversion
      if (
        clientConversion[columnKey]?.jsonParse &&
        typeof serverRow[columnKey] === "string"
      ) {
        currentRow[columnKey] = JSON.parse(serverRow[columnKey])
      } else {
        currentRow[columnKey] = serverRow[columnKey]
      }
    } else {
      if (
        clientConversion[columnKey]?.jsonParse &&
        typeof serverRow[columnKey] === "string"
      ) {
        // compare and parse and sync
        if (JSON.stringify(currentRow[columnKey]) !== serverRow[columnKey]) {
          changed.columns[columnKey] = true
          //rebuild.updatedKeys[rowKey] = true
          // we should be fine parsing without undefined checks... there never should be undefined as a server value
          // and as well server should not return empty string to be parsed. thats not valid. should be sent from server as null
          // (todo: take care of undefined when using localData in Table)
          currentRow[columnKey] = JSON.parse(serverRow[columnKey])
          if (
            checkForOtherModifications &&
            !clientConversion[columnKey]?.doNotCheckForOtherModification &&
            !changedColumns?.[columnKey] &&
            (currentRow.OvlSysState as OvlSysColumn).rowState.state === "Update"
          ) {
            otherModified.add(columnKey)
          }
        }
      } else {
        // just compare and sync
        if (currentRow[columnKey] !== serverRow[columnKey]) {
          changed.columns[columnKey] = true
          //rebuild.updatedKeys[rowKey] = true
          currentRow[columnKey] = serverRow[columnKey]
          if (
            checkForOtherModifications &&
            !clientConversion[columnKey]?.doNotCheckForOtherModification &&
            !changedColumns?.[columnKey] &&
            (currentRow.OvlSysState as OvlSysColumn).rowState.state === "Update"
          ) {
            otherModified.add(columnKey)
          }
        }
      }
    }
  })
  if (otherModified.size > 0) {
    let modifiedColumnList = Array.from(otherModified)
      .map((k) => k)
      .join(",")
    SnackAdd(`Some column(s) got modified meanwhile: ${modifiedColumnList}`)
  }
  rebuild.changed[rowKey] = changed
  return changed
}
